import React, { useEffect, useMemo, useState, useCallback, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ScanFlyerDialog from "@/components/ScanFlyerDialog";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { toast } from "sonner";
import { X, Plus, Trash2, ChevronLeft, ChevronRight, CalendarDays, List, LayoutGrid, Repeat } from "lucide-react";
import { getHouseUnlockPin } from "@/lib/houseLock";
import { isTeenPlusFamily, ageFromBirthday } from "@/lib/birthdays";
import { fmtTime } from "@/lib/time";
import ConfettiBurst from "@/components/ConfettiBurst";
import BirthdayPlanningBanner from "@/components/BirthdayPlanningBanner";
import TodayPulse from "@/components/TodayPulse";
import WeekViewGrid, { weekStartFromDate } from "@/components/WeekViewGrid";
import EventAttachmentPicker from "@/components/EventAttachmentPicker";
import MemberChipAvatar from "@/components/MemberChipAvatar";
import { memberAccentHex } from "@/lib/memberColors";

// Family-scoped UI prefs that need to be read deep in the render tree.
// Threading these as props through 6+ wrapper components creates a
// brittle signature chain on a 3000-line file; a tiny context with a
// single boolean keeps the wrappers untouched. Only `colorCodeByMember`
// for now — add more keys here if/when we add similar app-level
// preferences. Default is "off" so the legacy render path is the
// fallback when no provider is mounted (tests, storybook, etc.).
const CalendarPrefsContext = React.createContext({ colorCodeByMember: false });
import {
  recordEditorOpen,
  recordSectionExpand,
  getHabitDefaults,
} from "@/lib/eventEditorHabits";
import {
  CADENCE_PRESETS,
  cadencePresetLabel,
  presetForRrule,
  parseRrule,
  buildRruleFromCustom,
  withRruleEnd,
  summarizeRrule,
  WEEKDAY_TOKENS,
  WEEKDAY_LABELS,
  isVirtualInstanceId,
  masterIdOf,
} from "@/lib/rrule";
import {
  EVENT_KINDS,
  kindOf,
  kindLabel,
  eventEmoji,
  datetimeLocalToIso,
  isoToDatetimeLocal,
  fmtEventWhen,
  groupEventsByDay,
  dayLabel,
  mergeWithBirthdays,
  eventCountdown,
  localizeCountdown,
  visibleEventKinds,
  detectKindFromTitle,
} from "@/lib/events";

const emptyDraft = {
  title: "",
  kind: "activity",
  emoji: "",
  starts_at: "",
  ends_at: "",
  member_ids: [],
  notes: "",
  house_id: "",
  linked_list_id: "",
  linked_list_new_title: "",
  notify_member_ids: [],
  notify_on_day_member_ids: [],
  rrule: "",
  attachment_image: "",
};

import { useI18n } from "@/lib/i18n";

const sameYMD = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export default function CalendarSheet({ members, viewerId, onClose, autoOpenAdd = false, scrollToEventId = null, openEditEventId = null, requestDeleteEventId = null, inline = false, hasBYOKey = false, onOpenSettings = null }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  useSuspendPollingWhileOpen();
  const viewer = members.find((m) => m.id === viewerId);
  // Teens (kid role with a known age ≥ 11) get the same add/edit
  // powers as parents — they're managing their own football, exam
  // and friends-night schedule. Younger kids (under 11, or a kid
  // with no birthday set) stay read-only so they can't accidentally
  // wipe a Mum-added dentist appointment. Adults / no-viewer fall
  // through as parent.
  const viewerAge = viewer ? ageFromBirthday(viewer.birthday) : null;
  const isTeenOrOlder = viewer && viewer.role === "kid" && viewerAge != null && viewerAge >= 11;
  const isParent = !viewer || viewer.role === "parent" || isTeenOrOlder;

  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Ref to the inline editor card so we can implement
  // "tap outside to close" without prompting for confirmation —
  // any unsaved changes are discarded, mirroring the existing X / Cancel.
  const editorRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  // Tracks whether the user has manually tapped a kind chip. Auto-detect
  // only overrides while this stays false — once they've made a choice
  // we never silently reroute it from typing in the title field.
  const [kindTouched, setKindTouched] = useState(false);
  const [houses, setHouses] = useState([]);
  const [todoLists, setTodoLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Effective billing tier — drives the photo-attachment Plus gate.
  // Best-effort fetch on mount; falls back to "free" so a slow load
  // never accidentally hands a Free family the picker.
  const [tier, setTier] = useState("free");
  useEffect(() => {
    let live = true;
    api.billingStatus()
      .then((s) => { if (live) setTier(s?.tier || "free"); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  // Optional editor sections that start collapsed so the Add Event
  // form opens short and uncluttered. Each chip below the form
  // toggles its section open. When opening an existing event in
  // startEdit, sections auto-expand if their draft field has a
  // value, so a user editing a trip / note / linked list isn't
  // greeted by a hidden field.
  const [expandedSections, setExpandedSections] = useState({
    endsAt: false,
    notes: false,
    linkedList: false,
  });
  const showEndsAt = expandedSections.endsAt || !!draft.ends_at;
  const showNotes = expandedSections.notes || !!(draft.notes || "").trim();
  const showLinkedList =
    expandedSections.linkedList ||
    !!draft.linked_list_id ||
    !!(draft.linked_list_new_title || "").trim();
  // Calendar B-mode: track which day the family is currently focused on.
  // Defaults to today; tapping a day in the month grid or 7-day strip
  // narrows the upcoming list to just that day. The "View whole list"
  // chip flips back to the original chronological scroll.
  const [selectedDay, setSelectedDay] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  });
  const [calendarView, setCalendarView] = useState("day"); // "day" | "week" | "list"
  // ScanFlyer dialog visibility — opened by the "📸 Scan flyer" chip,
  // dismissed when the user finishes / cancels.
  const [showScanFlyer, setShowScanFlyer] = useState(false);
  // Week-view anchor — Mon of the week being shown. Defaults to this week.
  const [weekAnchor, setWeekAnchor] = useState(() => weekStartFromDate(new Date()));
  // Scope picker for editing recurring events: when present, the
  // user clicked Save on a virtual instance and we ask "this / future
  // / all" before committing.
  const [scopePrompt, setScopePrompt] = useState(null); // { kind: 'save'|'delete', payload, event }
  // Family settings — refreshed alongside the cross-device freshness
  // poll. We only care about `color_code_by_member` here (other
  // settings are owned by the FamilySettingsPanel), so a snapshot
  // fetched once on mount + refreshed on the same 30s tick is plenty.
  // Defaults to the "off" state so the legacy kind-colour calendar
  // renders before the first network round-trip completes.
  const [colorCodeByMember, setColorCodeByMember] = useState(false);
  // Inline conflict warning — replaces the previous noisy toast. When
  // the soft-conflict check finds an overlap, we stash the matches
  // here and render a calm "Heads up — Mum has the dentist at 4. Add
  // anyway?" panel inside the editor card. Tapping Save again (now
  // labelled "Add anyway") sends the event through. Cleared whenever
  // the editor opens, the draft's people/time changes, or on cancel.
  const [conflictWarning, setConflictWarning] = useState(null);
  // When the user tweaks the time or attendee list AFTER seeing the
  // conflict warning, drop the warning so the next Save click rechecks
  // (otherwise they'd silently bypass a now-different conflict).
  useEffect(() => {
    if (conflictWarning || draft._bypassConflicts) {
      setConflictWarning(null);
      if (draft._bypassConflicts) {
        setDraft((d) => ({ ...d, _bypassConflicts: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.starts_at, draft.ends_at, JSON.stringify(draft.member_ids || [])]);
  // Drop the warning whenever the editor closes — re-opening starts fresh.
  useEffect(() => {
    if (!addOpen) setConflictWarning(null);
  }, [addOpen]);
  // Filter panel — collapsed by default so the calendar opens "calm".
  // Expanded on tap; the chip surfaces the active state ("Sofie · trips")
  // so families know whether they're seeing a filtered view.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Past-events drawer — show 10 most recent by default, fold out for
  // the rest. Resets when the modal opens fresh so big lists don't
  // surprise on next open.
  const [pastExpanded, setPastExpanded] = useState(false);
  // Anchor for the events section. Tapping a day in the month grid OR
  // the bottom 7-day strip scrolls this into view so the family sees
  // the event cards immediately, instead of having to scroll past the
  // selectors. `block: "start"` puts the section title at the top of
  // the viewport with a comfortable margin.
  const eventsAnchorRef = useRef(null);
  const scrollToEvents = () => {
    requestAnimationFrame(() => {
      eventsAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };
  const selectDayAndScroll = (iso) => {
    setSelectedDay(iso);
    setCalendarView("day");
    scrollToEvents();
  };
  const [houseFilter, setHouseFilter] = useState("all");  // "all" | "none" | house_id
  const [memberFilter, setMemberFilter] = useState("all"); // "all" | member_id
  const [kindFilter, setKindFilter] = useState("all"); // "all" | kind key

  const load = useCallback(async () => {
    try {
      const data = await api.listEvents();
      setEvents(data);
    } catch (e) {
      // Single retry after 800ms — covers the brief deploy/network
      // blip case where the backend is up <1s later and a toast feels
      // alarmist. Only the SECOND failure surfaces an error.
      await new Promise((r) => setTimeout(r, 800));
      try {
        const data = await api.listEvents();
        setEvents(data);
      } catch (e2) {
        toast.error("Could not load events");
      }
    }
  }, []);

  useEffect(() => {
    load();
    api.listHouses().then(setHouses).catch(() => setHouses([]));
    api.listTodoLists().then(setTodoLists).catch(() => setTodoLists([]));
    api.listEventTemplates().then(setTemplates).catch(() => setTemplates([]));
    // Keep the Escape listener alive while the sheet is mounted, but
    // don't put `onClose` in the dep array — calendarOnly mode mounts
    // CalendarSheet inline and passes a fresh `() => {}` on every
    // parent render, which would cause an `unmount → mount` thrash
    // and re-fire `load()` on every keystroke. Read it from a ref so
    // we always close the LATEST handler without re-subscribing.
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Cross-device freshness — refetch events whenever the tab/PWA
  // regains visibility OR the browser window gets focus, AND poll a
  // cheap "calendar changed" stamp every 30s in the background.
  //
  // Before this, the calendar loaded ONCE on mount and never refetched,
  // so a new event added on Dad's phone wouldn't appear on Mum's phone
  // until she fully closed the PWA (which forces remount). Now any of
  // these happen automatically:
  //   · Foregrounding the tab/PWA after the screen unlocks
  //   · Switching back from another browser tab
  //   · A periodic background ping reports the server has new data
  // Refetches are debounced so a rapid focus/blur burst (iOS sometimes
  // fires both within 200ms) only triggers one network call.
  useEffect(() => {
    let lastFetch = 0;
    let knownChangedAt = null;
    let pollTimer = null;
    let cancelled = false;

    const maybeLoad = (reason) => {
      // Don't yank state out from under an open dialog / editor —
      // would close the keyboard on mobile and lose unsaved input.
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      // Debounce within 1.5s of the last refetch — protects against
      // visibilitychange+focus firing back to back.
      const now = Date.now();
      if (now - lastFetch < 1500) return;
      lastFetch = now;
      load();
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") maybeLoad("visible");
    };
    const onFocus = () => maybeLoad("focus");

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    // Instant-sync hook: MembersManager (and anywhere else that flips
    // a family-wide pref) dispatches this event after a successful
    // save. Without it the toggle takes up to 30s to show up on the
    // calendar because the poll below is the only refresher.
    const onSettingsUpdated = (e) => {
      const next = e?.detail?.color_code_by_member;
      if (typeof next === "boolean") setColorCodeByMember(next);
      else if (e?.detail?.settings) {
        setColorCodeByMember(!!e.detail.settings.color_code_by_member);
      }
    };
    window.addEventListener("famfeel:family-settings-updated", onSettingsUpdated);

    // Background poll — hits the cheap settings endpoint just to read
    // `events_last_changed_at`. If that's newer than what we last
    // saw, kick off a full event refetch. Skipped when tab is hidden
    // so we don't drain battery on a backgrounded PWA.
    const pollOnce = async () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      try {
        const s = await api.getFamilySettings();
        const stamp = s?.events_last_changed_at || null;
        if (stamp && knownChangedAt && stamp !== knownChangedAt) {
          maybeLoad("changed-at");
        }
        if (stamp) knownChangedAt = stamp;
        // Pick up the color-code toggle on every poll so flipping
        // it in another tab takes effect within 30s without
        // requiring a full refresh.
        setColorCodeByMember(!!s?.color_code_by_member);
      } catch {
        /* network blip — try again next tick */
      }
    };
    pollOnce(); // seed knownChangedAt + initial reconcile
    pollTimer = setInterval(pollOnce, 30_000);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("famfeel:family-settings-updated", onSettingsUpdated);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [load]);


  // Auto-open the "Add event" form when the sheet is opened from the
  // dashboard "+ Add" shortcut. Runs once on mount; non-parents can't add
  // events so we honour that too.
  useEffect(() => {
    if (autoOpenAdd && isParent) {
      openAdd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When opened from a tapped EventPill OR EventInfoPage's "Open in
  // calendar" button, jump to that event's day group.
  //
  // Three subtleties matter:
  //  1. Day groups key by **local** date (see lib/events.js
  //     groupEventsByDay), so we must derive the same local YYYY-MM-DD
  //     from the event's ISO start. Naively slicing `starts_at` yields
  //     UTC, which mis-keys events near midnight in non-UTC zones and
  //     scrolls to nothing → page sits at the top.
  //  2. Past events live behind a folded "past events" drawer. If the
  //     target is in the past we open that drawer first so its day
  //     group actually renders before we try to scroll to it.
  //  3. The default calendarView is "day" — which only renders a single
  //     day group (the currently selected day), so a future event's
  //     `ff-daygroup-XXX` anchor never exists. We flip to the flat
  //     "list" view (which renders every upcoming day group) so the
  //     target is actually in the DOM before we scroll. We also point
  //     `selectedDay` at the event's date for when the user flips back.
  //
  // We retry a few rAFs because the modal animates in and the events
  // list arrives async; the day-group node may not be on screen yet on
  // the first frame.
  useEffect(() => {
    if (!scrollToEventId || !events) return;
    const ev = events.find((x) => x.id === scrollToEventId);
    if (!ev) return;
    const start = new Date(ev.starts_at);
    if (Number.isNaN(start.getTime())) return;
    const dateKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
    const todayMid = new Date();
    todayMid.setHours(0, 0, 0, 0);
    const isPast = (new Date(ev.ends_at || ev.starts_at)) < todayMid;
    if (isPast) setPastExpanded(true);
    setCalendarView("list");
    setSelectedDay(dateKey);
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(`ff-daygroup-${dateKey}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 20) {
        // ~20 frames ≈ 330ms — covers the modal entry animation, the
        // day-view-to-list-view switch re-render, and the past-drawer
        // expansion. Still cheap enough to bail quickly when the
        // target legitimately isn't in the DOM (e.g. filtered out).
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
    };
  }, [scrollToEventId, events]);

  // Deep-link from EventInfoPage's Edit button. When the parent taps
  // Edit on the event page we navigate back to /app with
  // location.state.calendarOpenEditEventId set — Dashboard passes the
  // id through here, we find the event in the loaded list and open
  // the edit drawer. Each id is consumed exactly ONCE — without the
  // ref, a downstream `events` swap (e.g. from `load()` after a Save)
  // would re-fire the effect with the same id and re-open the editor
  // on top of itself, making the editor appear to "not close after
  // Save". Same trap fires after deleting an unrelated event when an
  // earlier edit had stamped this prop.
  const consumedEditIdRef = useRef(null);
  useEffect(() => {
    if (!openEditEventId || !events) return;
    if (consumedEditIdRef.current === openEditEventId) return;
    const ev = events.find((x) => x.id === openEditEventId);
    if (!ev) return;
    consumedEditIdRef.current = openEditEventId;
    openEdit(ev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditEventId, events]);

  const openAdd = () => {
    setEditingId(null);
    setConflictWarning(null);
    // Seed defaults from the device's habit profile (post-7-day ripening).
    // Until enough signal accumulates this is { false, false, false }.
    setExpandedSections(getHabitDefaults());
    recordEditorOpen();
    // Default-to-selected-day: if the user has a non-today day pinned
    // in the month grid (e.g. they tapped May 20 to peek at it), the
    // big "+ Add event" button should add to THAT day at 9:00, not
    // bounce them back to today's clock. Today still defaults to the
    // next-hour timed slot so spur-of-the-moment adds keep their old
    // behaviour. `selectedDay` is "YYYY-MM-DD" in family-local time.
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    let startsAtIso;
    if (selectedDay && selectedDay !== todayKey) {
      // Pinned future/past day → 9:00 local on that day.
      const [y, m, d] = selectedDay.split("-").map((n) => parseInt(n, 10));
      const at = new Date(y, (m || 1) - 1, d || 1, 9, 0, 0, 0);
      startsAtIso = at.toISOString();
    } else {
      // Today (or no selection): next round hour.
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      startsAtIso = now.toISOString();
    }
    setDraft({
      ...emptyDraft,
      starts_at: isoToDatetimeLocal(startsAtIso),
    });
    // Fresh form: auto-detect is allowed to fire on title typing.
    setKindTouched(false);
    setAddOpen(true);
  };

  const openEdit = (ev) => {
    if (!isParent) return;
    setConflictWarning(null);
    // Allow live-switching: clicking another event row while the
    // editor is already open swaps to that event instead of opening a
    // second form. We treat any pending change as cheap-to-lose
    // (parent still hasn't pressed Save) but warn once if the title
    // was edited and they haven't saved yet.
    if (addOpen && editingId && editingId !== ev.id) {
      const currentSavedTitle = (events || []).find((e) => e.id === editingId)?.title || "";
      if (draft.title.trim() && draft.title.trim() !== currentSavedTitle) {
        const ok = window.confirm("You have unsaved changes here. Switch to the other event anyway?");
        if (!ok) return;
      }
    }
    setEditingId(ev.id);
    // Editing an existing event — kind is already saved, never auto-override.
    setKindTouched(true);
    setExpandedSections({ endsAt: false, notes: false, linkedList: false });
    setDraft({
      title: ev.title,
      kind: ev.kind || "other",
      emoji: ev.emoji || "",
      starts_at: isoToDatetimeLocal(ev.starts_at),
      ends_at: ev.ends_at ? isoToDatetimeLocal(ev.ends_at) : "",
      member_ids: ev.member_ids || [],
      notes: ev.notes || "",
      house_id: ev.house_id || "",
      linked_list_id: ev.linked_list_id || "",
      linked_list_new_title: "",
      notify_member_ids: ev.notify_member_ids || [],
      notify_on_day_member_ids: ev.notify_on_day_member_ids || [],
      rrule: ev.rrule || "",
      attachment_image: ev.attachment_image || "",
      _isInstance: !!ev.is_recurring_instance,
      _masterId: ev.master_id || null,
      _instanceIso: ev.recurrence_original_iso || null,
    });
    setAddOpen(true);
  };

  // Tap-outside-to-close: when the inline editor is open, any pointerdown
  // outside the editor card closes it (same behaviour as Cancel / X).
  // We deliberately do NOT prompt for unsaved changes — the request
  // here is "I just want to bail", and the visible Save button is right
  // there at the top of the card if they meant to keep the edit.
  // Skip: native date/time picker popouts and emoji pickers can render
  // outside the card via portals; ignore any pointerdown that originates
  // from such a popover by walking up to find a [data-ff-form-popover] hint.
  // Outside-click handling intentionally REMOVED. Earlier versions
  // closed the editor on any tap outside the card, but accidental
  // taps (especially on phone, where the bottom-sheet sits over the
  // calendar grid) were causing real users to lose multiple
  // minutes of typed-in event details. Saving on outside-click is
  // also wrong — drafts can be partial, conflict warnings need
  // acknowledgement, and Plus features (photo attach) need a
  // deliberate confirmation. So now: bail-out is always explicit
  // via the X button (top), the Cancel button (bottom), or a
  // browser back gesture. Both are visible at all times.
  // (Live-switching between events while editing is still
  // handled by `openEdit`, which warns once if the title was
  // changed.)


  // Tapping an empty day in the month grid opens the add-event form
  // with that date prefilled at 9:00 local — the most common "school /
  // family event" start time, and easy to adjust if it's not.
  const openAddForDate = (dateKey) => {
    if (!isParent) return;
    setEditingId(null);
    setExpandedSections(getHabitDefaults());
    recordEditorOpen();
    const [y, m, d] = dateKey.split("-").map((n) => parseInt(n, 10));
    const at = new Date(y, (m || 1) - 1, d || 1, 9, 0, 0, 0);
    setDraft({
      ...emptyDraft,
      starts_at: isoToDatetimeLocal(at.toISOString()),
    });
    // Fresh form: re-enable title→kind auto-detect. (Without this,
    // any prior edit/manual chip-tap left `kindTouched=true`, which
    // silently suppressed the auto-pick on every subsequent
    // tap-a-day → add. Day-grid is the most-used add path so missing
    // this reset broke the feature for everyone in steady-state use.)
    setKindTouched(false);
    setAddOpen(true);
    // Scroll the form into view since it lives higher up than the grid.
    setTimeout(() => {
      const sheet = document.querySelector('[data-testid="calendar-sheet"] > div');
      if (sheet) sheet.scrollTo({ top: 0, behavior: "smooth" });
    }, 30);
  };

  // Apply a saved template to the current draft. Sets the start time
  // to "in 1 hour, rounded down to the next half-hour" so the parent
  // doesn't have to tweak the time-picker for the common "later today"
  // case. Duration replays as ends_at = starts + duration_minutes.
  const applyTemplate = (t) => {
    const now = new Date();
    now.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0);
    const end = new Date(now.getTime() + (t.duration_minutes || 60) * 60_000);
    setDraft({
      ...emptyDraft,
      title: t.title || "",
      kind: t.kind || "activity",
      emoji: t.emoji || "",
      starts_at: isoToDatetimeLocal(now.toISOString()),
      ends_at: isoToDatetimeLocal(end.toISOString()),
      member_ids: t.member_ids || [],
      notes: t.notes || "",
      rrule: t.rrule || "",
    });
    // Template carries an explicit kind chosen by the user when they
    // saved it — lock it so editing the title afterwards doesn't
    // silently re-route the kind via auto-detect.
    setKindTouched(true);
    toast.success(`Filled from "${t.name}"`);
  };

  // Save the current draft as a reusable template. Names default to
  // the event's title; user can rename via the prompt for clarity.
  const saveAsTemplate = async () => {
    const defaultName = (draft.title || "").trim().slice(0, 60);
    if (!defaultName) {
      toast.error("Give it a title first");
      return;
    }
    const name = window.prompt("Name this template", defaultName);
    if (!name || !name.trim()) return;
    let durationMinutes = 60;
    if (draft.starts_at && draft.ends_at) {
      const s = new Date(datetimeLocalToIso(draft.starts_at));
      const e = new Date(datetimeLocalToIso(draft.ends_at));
      if (!isNaN(s) && !isNaN(e) && e > s) {
        durationMinutes = Math.max(15, Math.round((e - s) / 60_000));
      }
    }
    try {
      const created = await api.createEventTemplate({
        name: name.trim(),
        title: draft.title.trim(),
        kind: draft.kind,
        emoji: (draft.emoji || "").trim() || null,
        member_ids: draft.member_ids || [],
        notes: (draft.notes || "").trim() || null,
        duration_minutes: durationMinutes,
        rrule: draft.rrule || null,
      });
      setTemplates((prev) => [created, ...prev]);
      toast.success("Saved as template");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save template");
    }
  };

  const save = async () => {
    if (!draft.title.trim()) {
      toast.error("Give it a title");
      return;
    }
    if (!draft.starts_at) {
      toast.error("Pick a start time");
      return;
    }
    const payload = {
      title: draft.title.trim(),
      kind: draft.kind,
      emoji: (draft.emoji || "").trim(),
      starts_at: datetimeLocalToIso(draft.starts_at),
      ends_at: draft.ends_at ? datetimeLocalToIso(draft.ends_at) : null,
      member_ids: draft.member_ids,
      notify_member_ids: (draft.notify_member_ids || []).filter((id) =>
        (draft.member_ids || []).includes(id)
      ),
      notify_on_day_member_ids: (draft.notify_on_day_member_ids || []).filter(
        (id) => (draft.member_ids || []).includes(id)
      ),
      notes: draft.notes.trim() || null,
      house_id: draft.house_id || "",
      attachment_image: draft.attachment_image || "",
      // Recurrence: send empty string to clear, value to set, omit
      // (handled below) to leave alone. For new events we always
      // send to seed the field; for edits of recurring instances
      // we'll let the scope picker decide.
      rrule: draft.rrule || "",
    };
    // Stamp the active viewer as creator on new events so the backend
    // can silent-merge two parents writing the same event in the same
    // window (e.g. Mum and Dad both adding "Ballet at 5"). Edits
    // intentionally leave this alone — we never rewrite original
    // authorship.
    if (!editingId && viewerId) {
      payload.created_by = viewerId;
    }
    // House-lock: carry the relevant house PINs with the write. For a
    // new event, that's the destination house's PIN. For an edit,
    // also the originally-assigned house's PIN (in case it's different).
    const destHousePin = draft.house_id ? getHouseUnlockPin(draft.house_id) : null;
    if (destHousePin) {
      payload.house_pin = destHousePin;
      payload.house_pin_dest = destHousePin;
    }
    if (editingId) {
      const orig = (events || []).find((e) => e.id === editingId);
      if (orig?.house_id && orig.house_id !== draft.house_id) {
        const origPin = getHouseUnlockPin(orig.house_id);
        if (origPin) payload.house_pin = origPin;
        if (destHousePin) payload.house_pin_dest = destHousePin;
      }
    }
    setBusy(true);
    // Soft conflict check — warn the parent if any tagged person is
    // already booked in the same window. We only ASK once, on first
    // save click; pressing Save again with the same draft sends it
    // through (state below tracks "user already saw the warning").
    if (draft.member_ids?.length && draft.starts_at && !draft._bypassConflicts) {
      try {
        const res = await api.checkEventConflicts({
          starts_at: datetimeLocalToIso(draft.starts_at),
          ends_at: draft.ends_at ? datetimeLocalToIso(draft.ends_at) : null,
          member_ids: draft.member_ids,
          exclude_event_id: editingId || null,
        });
        if (res.conflicts && res.conflicts.length) {
          // Calm inline warning instead of a 409-shaped toast.
          // The button below relabels to "Add anyway" so the user
          // can confirm. Marking _bypassConflicts here means the
          // next save click skips this check entirely.
          setConflictWarning({ conflicts: res.conflicts });
          setDraft({ ...draft, _bypassConflicts: true });
          setBusy(false);
          return;
        }
      } catch {
        // Best-effort: don't block save if the conflict-check API fails.
      }
    }
    try {
      // Resolve the linked list before saving the event. Three cases:
      //   • User picked an existing list → use that id directly.
      //   • User typed a new-list title → create the list first, link to it.
      //   • User cleared the link → pass empty string so the API unlinks.
      let resolvedListId = draft.linked_list_id;
      const newListTitle = (draft.linked_list_new_title || "").trim();
      if (newListTitle) {
        try {
          const created = await api.createTodoList({ title: newListTitle });
          resolvedListId = created.id;
          // Refresh the cached lists so the picker reflects the new entry
          // immediately if the user re-opens the editor.
          setTodoLists((prev) => (prev ? [...prev, created] : [created]));
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Couldn't create the list");
          setBusy(false);
          return;
        }
      }
      // Pass linked_list_id explicitly only when the user actually
      // touched it. For a new event without any link the field stays
      // out of the payload (None on the server).
      if (editingId) {
        // Always include on edit so user can both link and unlink.
        payload.linked_list_id = resolvedListId || "";
      } else if (resolvedListId) {
        payload.linked_list_id = resolvedListId;
      }
      if (editingId) {
        // Recurring scope: when editing a virtual instance, pop a
        // dialog asking "this / future / all" and stash the payload
        // for the second click. Master-event edits skip the prompt.
        if (draft._isInstance && !draft._scope) {
          setScopePrompt({ kind: "save", payload, draft: { ...draft } });
          setBusy(false);
          return;
        }
        if (draft._isInstance && draft._scope) {
          payload.scope = draft._scope;
          payload.instance_starts_at = draft._instanceIso;
        }
        await api.updateEvent(editingId, payload);
        toast.success("Updated");
      } else {
        const created = await api.createEvent(payload);
        // Silent-merge detection: when the response's creator isn't
        // me but my id landed in `also_added_by_member_ids`, the
        // backend folded my submission into someone else's existing
        // event. Surface a calm "Undo" affordance for 30s so I can
        // split it back out if the merge was wrong (e.g. we meant
        // two separate appointments at the same minute).
        const wasMerged =
          created &&
          viewerId &&
          created.created_by &&
          created.created_by !== viewerId &&
          (created.also_added_by_member_ids || []).includes(viewerId);
        if (wasMerged) {
          // Capture the original payload so Undo can recreate it as
          // a standalone row (without re-triggering the same merge).
          const originalPayload = { ...payload, bypass_dedupe: true };
          const otherCreator = members.find((m) => m.id === created.created_by);
          const mergedWithName = otherCreator?.name || "another parent";
          toast(`Merged with ${mergedWithName}`, {
            description: "Same time, same title — folded into one card.",
            duration: 30_000,
            action: {
              label: "Undo · Split out",
              onClick: async () => {
                try {
                  await api.unmergeEvent(created.id, viewerId);
                  await api.createEvent(originalPayload);
                  toast.success("Split out — both events kept");
                  await load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't split out");
                }
              },
            },
            // Visible identifier for tests + a11y screen-reader label.
            id: `merge-undo-${created.id}`,
          });
        } else {
          toast.success("Added");
        }
      }
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ev) => {
    // Recurring instance → ask "this / future / all" before deleting.
    if (ev.is_recurring_instance && ev.master_id) {
      setScopePrompt({ kind: "delete", event: ev });
      return;
    }
    if (!window.confirm(`Delete “${ev.title}”?`)) return;
    try {
      const pin = ev.house_id ? getHouseUnlockPin(ev.house_id) : null;
      await api.deleteEvent(ev.id, pin ? { house_pin: pin } : {});
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  // Confirmation handler for the "this / future / all" picker.
  const confirmScope = async (scopeChoice) => {
    if (!scopePrompt) return;
    try {
      if (scopePrompt.kind === "save") {
        const { payload, draft: d } = scopePrompt;
        const finalPayload = {
          ...payload,
          scope: scopeChoice,
          instance_starts_at: d._instanceIso,
        };
        // For "this" scope, suppress rrule changes (override is per-instance).
        if (scopeChoice === "this") delete finalPayload.rrule;
        await api.updateEvent(editingId, finalPayload);
        toast.success(scopeChoice === "this" ? "This event updated" : scopeChoice === "future" ? "From here forward updated" : "All occurrences updated");
        setScopePrompt(null);
        setAddOpen(false);
        await load();
        return;
      }
      // delete
      const ev = scopePrompt.event;
      const pin = ev.house_id ? getHouseUnlockPin(ev.house_id) : null;
      const opts = { scope: scopeChoice };
      if (pin) opts.house_pin = pin;
      await api.deleteEvent(ev.id, opts);
      toast.success(scopeChoice === "this" ? "This event removed" : scopeChoice === "future" ? "From here forward removed" : "Series deleted");
      setScopePrompt(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply");
    }
  };

  // Deep-link from EventInfoPage's Delete button (recurring instances
  // only — single events are deleted directly on the event page).
  // Routes through the existing `remove` handler which fires the
  // "this / future / all" scope picker for instances. Lives below
  // `remove` declaration to keep React/Babel happy in production
  // builds (no temporal-dead-zone risk). Also guarded with a "last
  // consumed id" ref so an `events` swap (post-delete `load()`)
  // doesn't re-invoke the scope picker against a stale id.
  const consumedDeleteIdRef = useRef(null);
  useEffect(() => {
    if (!requestDeleteEventId || !events) return;
    if (consumedDeleteIdRef.current === requestDeleteEventId) return;
    const ev = events.find((x) => x.id === requestDeleteEventId);
    if (!ev) return;
    consumedDeleteIdRef.current = requestDeleteEventId;
    remove(ev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDeleteEventId, events]);

  // Patch handler used by Week-view drag/resize AND month-grid drop.
  // When `dropDateKey` is supplied (month-grid drop), shift the
  // event's date but keep the time-of-day intact.
  const patchEventDrag = async (ev, fields) => {
    try {
      const isInstance = !!ev.is_recurring_instance && !!ev.master_id;
      const opts = { ...fields };
      const pin = ev.house_id ? getHouseUnlockPin(ev.house_id) : null;
      if (pin) {
        opts.house_pin = pin;
        opts.house_pin_dest = pin;
      }
      if (isInstance) {
        opts.scope = "this";
        opts.instance_starts_at = ev.recurrence_original_iso;
      }
      await api.updateEvent(ev.id, opts);
      toast.success("Moved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move");
      await load();
    }
  };

  const moveEventToDate = async (ev, dropDateKey) => {
    if (!ev || !dropDateKey) return;
    if (ev.source === "birthday") return;
    // Shift starts_at + ends_at by the day-delta, preserving HH:MM.
    const start = new Date(ev.starts_at);
    if (Number.isNaN(start.getTime())) return;
    const [y, m, d] = dropDateKey.split("-").map(Number);
    const newStart = new Date(y, m - 1, d, start.getHours(), start.getMinutes(), 0, 0);
    if (sameYMD(newStart, start)) return;
    let newEnd = null;
    if (ev.ends_at) {
      const end = new Date(ev.ends_at);
      const dur = end.getTime() - start.getTime();
      newEnd = new Date(newStart.getTime() + dur);
    }
    await patchEventDrag(ev, {
      starts_at: newStart.toISOString(),
      ends_at: newEnd ? newEnd.toISOString() : null,
    });
  };

  const toggleMember = (id) => {
    setDraft((d) => ({
      ...d,
      member_ids: d.member_ids.includes(id)
        ? d.member_ids.filter((x) => x !== id)
        : [...d.member_ids, id],
    }));
  };

  // Merge real events with synthetic birthday events first, then apply the
  // three filters (house / member / kind). Birthdays have kind="birthday" and
  // member_ids=[their_id], so they compose naturally with the filters.
  const allEvents = events ? mergeWithBirthdays(events, members) : null;
  const merged = allEvents
    ? allEvents.filter((ev) => {
        if (houseFilter !== "all" && houses.length > 0) {
          // Birthdays are virtual + never house-scoped; show them only on "all".
          if (ev.source === "birthday") return false;
          if (houseFilter === "none" ? !!ev.house_id : ev.house_id !== houseFilter) return false;
        }
        if (memberFilter !== "all") {
          const attendees = ev.member_ids || [];
          if (!attendees.includes(memberFilter)) return false;
        }
        if (kindFilter !== "all") {
          if ((ev.kind || "other") !== kindFilter) return false;
        }
        return true;
      })
    : null;
  const grouped = merged ? groupEventsByDay(merged) : [];
  const now = new Date();
  // "All upcoming" should mean "starting today or later". Events
  // grouped under a past date (e.g. a multi-day trip that started
  // last week but is still ongoing) belong to the past list, not
  // the upcoming list — Display Mode's away strip already surfaces
  // those ambient situations. Without this filter the upcoming
  // list grew stale: every old multi-day event lingered at the top
  // for days after its start date had passed.
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const upcomingGroups = grouped.filter((g) => g.date >= todayKey);
  const pastGroups = grouped
    .filter((g) => g.date < todayKey)
    .reverse();

  // Teen-plus families get a subtle fade on the event-row icon tints so
  // the month doesn't look like a preschooler's placemat. Computed once
  // per render; the `members` list rarely changes in-session.
  // Teen+ families historically had a faded calendar palette and a
  // more compact layout. As of Jun '26 the user wants the faded
  // pastel palette to apply to ALL families (it reads cleaner against
  // the new cool-paper background). We keep the *layout* coupling
  // intact — `smallAvatars` + `hasKid` still flip based on the real
  // teen-plus signal so families with young kids still get big
  // avatars and the "Kids" event kind.
  const isTeenLayout = isTeenPlusFamily(members);
  const fadeTint = true;
  const smallAvatars = isTeenLayout;
  const hasKid = !isTeenLayout;
  const kindChoices = useMemo(() => visibleEventKinds({ hasKid }), [hasKid]);

  // Auto-prune past events beyond the 25-row cap. Non-birthday,
  // non-virtual events only — birthdays are synthesized from the member
  // list and virtual events don't have server rows to delete. Runs once
  // per drawer open (guarded by a ref) so re-renders don't re-fire it.
  // Parents only — kids shouldn't trigger mass deletes. Fire-and-forget;
  // we don't block the UI on the cleanup.
  const prunedRef = React.useRef(false);
  useEffect(() => {
    if (!isParent) return;
    if (prunedRef.current) return;
    const flat = pastGroups.flatMap((g) => g.items);
    const toDelete = flat
      .slice(25)
      .filter((ev) => ev && ev.id && ev.source !== "birthday");
    if (toDelete.length === 0) return;
    prunedRef.current = true;
    (async () => {
      for (const ev of toDelete) {
        try {
          await api.deleteEvent(ev.id);
        } catch {
          /* silent — best effort cleanup */
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParent, pastGroups.length]);

  // Shared content rendered in both inline and modal shells.
  const content = (
    <div className="px-5 md:px-7 py-5 space-y-5">
      <TodayPulse events={merged || events || []} members={members} />
      <BirthdayPlanningBanner members={members} events={merged || events || []} />
          {isParent && (
            <div className="flex items-center gap-2 flex-wrap">
              {!addOpen && (
                <>
                  <button
                    onClick={openAdd}
                    className="ff-chip is-active"
                    data-testid="calendar-add"
                    style={{ paddingLeft: "1rem", paddingRight: "1.125rem" }}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> {t({ en: "Add event", da: "Tilføj begivenhed" })}
                  </button>
                  {/* Scan flyer — Skylight-killer feature. Tap → camera → AI
                      reads dates/titles → preview → bulk-add. Uses the
                      family's bring-your-own Google AI key (zero cost to
                      FamFeel; Google's free tier easily covers normal use).
                      The button itself shows for every parent — if a key
                      isn't connected yet, the dialog routes them to
                      Settings before doing anything else. */}
                  <button
                    onClick={() => setShowScanFlyer(true)}
                    className="ff-chip"
                    data-testid="calendar-scan-flyer"
                    title="Take a picture of a school flyer or sports schedule and we'll lift the events into your calendar"
                  >
                    📸 Scan flyer
                  </button>
                </>
              )}
              {addOpen && (
                <div ref={editorRef} className="ff-card p-5 space-y-4 relative w-full">
                  {/* Sticky top header — gives a quick X close + Save
                      so the user never has to scroll to the very bottom
                      to commit or bail out. Mirrors the actions at the
                      bottom 1:1; outside-click no longer closes the
                      card (too many accidental losses of typed-in
                      details), so X / Save / Cancel are the only
                      ways out. `ff-bg` matches the card background
                      so the sticky header doesn't show seams when
                      content scrolls. */}
                  <div
                    className="sticky -top-5 -mx-5 -mt-5 mb-1 px-5 py-3 flex items-center justify-between gap-3 bg-[color:var(--surface)] border-b border-[color:var(--line-soft)] rounded-t-2xl z-10"
                    data-testid="event-editor-sticky-header"
                  >
                    <button
                      type="button"
                      onClick={() => setAddOpen(false)}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-[color:var(--bg-soft)] transition-colors"
                      data-testid="event-close-top"
                      aria-label={editingId
                        ? t({ en: "Close edit", da: "Luk redigering" })
                        : t({ en: "Close add event", da: "Luk tilføj begivenhed" })}
                    >
                      <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                    <div className="label-tiny truncate">
                      {editingId
                        ? t({ en: "Edit event", da: "Rediger begivenhed" })
                        : t({ en: "Add event", da: "Tilføj begivenhed" })}
                    </div>
                    <button
                      type="button"
                      onClick={save}
                      disabled={busy}
                      className="ff-btn ff-btn-primary h-8 px-4 text-[13px]"
                      data-testid="event-save-top"
                    >
                      {busy
                        ? t({ en: "Saving…", da: "Gemmer…" })
                        : editingId
                          ? t({ en: "Save", da: "Gem" })
                          : t({ en: "Add", da: "Tilføj" })}
                    </button>
                  </div>
                  {/* Templates picker — only when adding a new event AND
                      at least one template has been saved. Tap a chip
                      to one-shot fill the form. */}
                  {!editingId && templates.length > 0 && (
                    <div data-testid="event-templates-picker">
                      <label className="label-tiny block mb-2">
                        {t({ en: "Quick start from a template", da: "Hurtig start ud fra en skabelon" })}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {templates.map((tmpl) => (
                          <button
                            key={tmpl.id}
                            type="button"
                            onClick={() => applyTemplate(tmpl)}
                            className="ff-chip"
                            data-testid={`event-template-${tmpl.id}`}
                            title={tmpl.title}
                          >
                            <span className="text-base">{tmpl.emoji || kindOf(tmpl.kind).emoji}</span>{" "}
                            {tmpl.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="label-tiny block mb-2">
                      {t({ en: "Title", da: "Titel" })}
                    </label>
                    <input
                      type="text"
                      value={draft.title}
                      onChange={(e) => {
                        const next = e.target.value;
                        setDraft((d) => {
                          // Auto-pick the kind from the title only
                          // until the user explicitly picks one.
                          // Once `kindTouched` is true, we never
                          // override their choice. Editing an
                          // existing event is also off-limits — we
                          // shouldn't second-guess what they
                          // already saved.
                          let nextKind = d.kind;
                          if (!editingId && !kindTouched) {
                            const guess = detectKindFromTitle(next, { hasKid });
                            // No-match (null) keeps the current
                            // default rather than wiping it.
                            if (guess) nextKind = guess;
                          }
                          return { ...d, title: next, kind: nextKind };
                        });
                      }}
                      placeholder={t({ en: "e.g. Sofie swim practice", da: "fx Sofies svømning" })}
                      className="ff-input"
                      maxLength={80}
                      autoFocus
                      data-testid="event-title"
                    />
                  </div>
                  <div>
                    <label className="label-tiny block mb-2">
                      {t({ en: "Kind", da: "Kategori" })}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {kindChoices.map((k) => {
                        const active = draft.kind === k.key;
                        // Each kind chip paints in its category tint so the
                        // picker reads as "1 colour per category" at a glance.
                        // Teen-plus families dim the tint 50% toward paper.
                        const bg = active
                          ? undefined // active chip keeps the black pill style
                          : fadeTint
                            ? `color-mix(in srgb, var(--bg) 50%, ${k.tint})`
                            : k.tint;
                        return (
                          <button
                            key={k.key}
                            onClick={() => {
                              setDraft({ ...draft, kind: k.key });
                              setKindTouched(true);
                            }}
                            className={`ff-chip ff-chip-sm ${active ? "is-active" : ""}`}
                            style={bg ? { background: bg } : undefined}
                            data-testid={`event-kind-${k.key}`}
                          >
                            <span className="text-[13px]">{k.emoji}</span>{" "}
                            {kindLabel(k, lang)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Optional per-event emoji override — shines for the
                      drab "Other" kind (default •) but works on any kind.
                      Short input, one glyph; clears itself back to the
                      kind's default when left empty. */}
                  <div>
                    <label className="label-tiny block mb-2">
                      {t({ en: "Custom icon (optional)", da: "Eget ikon (valgfrit)" })}
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        value={draft.emoji}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            // Keep it to a single grapheme so pasting a long
                            // string doesn't land in the card layout.
                            emoji: Array.from(e.target.value).slice(0, 1).join(""),
                          })
                        }
                        placeholder="🎵"
                        className="ff-input text-xl text-center tabular w-16"
                        maxLength={4}
                        data-testid="event-emoji"
                        aria-label="Custom emoji"
                      />
                      <div className="flex gap-1 flex-wrap">
                        {[
                          "⛷️", "🌻", "🍝", "☕️", "🏕", "🎃", "🎄",
                          "🎾", "🎨", "🎶", "🎬", "✂️", "🩺", "🦷", "📚",
                          "🚗", "🏡", "⛪️", "🎫", "🛍",
                        ].map((e) => (
                          <button
                            key={e}
                            type="button"
                            onClick={() => setDraft({ ...draft, emoji: e })}
                            className="text-lg leading-none p-1.5 rounded-md hover:bg-[color:var(--bg-soft)] transition-colors"
                            data-testid={`event-emoji-preset-${e}`}
                            aria-label={`Use ${e}`}
                          >
                            {e}
                          </button>
                        ))}
                        {draft.emoji && (
                          <button
                            type="button"
                            onClick={() => setDraft({ ...draft, emoji: "" })}
                            className="label-tiny px-2 py-1 rounded-md hover:bg-[color:var(--bg-soft)]"
                            data-testid="event-emoji-clear"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2 flex items-center gap-2">
                      {/* All-day toggle. The codebase already infers
                          all-day from a 00:00 start time (see
                          fmtEventWhen in lib/events.js), so flipping
                          this on simply swaps the time portion of
                          starts_at / ends_at to 00:00 and renders
                          plain date inputs. Flipping back restores a
                          sensible default time (9:00 / +1h). */}
                      {(() => {
                        const isAllDay = (draft.starts_at || "").endsWith("T00:00");
                        return (
                          <label
                            className="inline-flex items-center gap-2 cursor-pointer select-none"
                            data-testid="event-all-day-row"
                          >
                            <input
                              type="checkbox"
                              checked={isAllDay}
                              onChange={(e) => {
                                const on = e.target.checked;
                                const dateOf = (v) => (v || "").slice(0, 10);
                                if (on) {
                                  const sd = dateOf(draft.starts_at) || dateOf(new Date().toISOString());
                                  const ed = dateOf(draft.ends_at);
                                  setDraft({
                                    ...draft,
                                    starts_at: `${sd}T00:00`,
                                    ends_at: ed ? `${ed}T00:00` : "",
                                  });
                                } else {
                                  // Re-enable a sensible default time.
                                  const sd = dateOf(draft.starts_at) || dateOf(new Date().toISOString());
                                  const ed = dateOf(draft.ends_at);
                                  setDraft({
                                    ...draft,
                                    starts_at: `${sd}T09:00`,
                                    ends_at: ed ? `${ed}T10:00` : "",
                                  });
                                }
                              }}
                              data-testid="event-all-day-toggle"
                              className="w-4 h-4"
                            />
                            <span className="label-tiny">
                              {t({ en: "All day", da: "Hele dagen" })}
                            </span>
                          </label>
                        );
                      })()}
                    </div>
                    {(() => {
                      const isAllDay = (draft.starts_at || "").endsWith("T00:00");
                      const weekdayHint = (iso) => {
                        if (!iso) return "";
                        try {
                          const d = new Date(
                            iso.length === 10 ? `${iso}T00:00:00` : iso,
                          );
                          if (Number.isNaN(d.getTime())) return "";
                          // e.g. "Sat · 9 May" — gives the parent the
                          // weekday name without leaving the form.
                          return d.toLocaleDateString(undefined, {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          });
                        } catch {
                          return "";
                        }
                      };
                      const startsHint = weekdayHint(draft.starts_at);
                      const endsHint = weekdayHint(draft.ends_at);
                      return (
                        <>
                          <div>
                            <label className="label-tiny block mb-2">
                              {t({ en: "Starts", da: "Starter" })}
                            </label>
                            <input
                              type={isAllDay ? "date" : "datetime-local"}
                              value={
                                isAllDay
                                  ? (draft.starts_at || "").slice(0, 10)
                                  : draft.starts_at
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                setDraft({
                                  ...draft,
                                  starts_at: isAllDay && v ? `${v}T00:00` : v,
                                });
                              }}
                              className="ff-input tabular"
                              data-testid="event-starts-at"
                            />
                            {startsHint && (
                              <div
                                className="label-tiny mt-1 text-[color:var(--ink-3)]"
                                data-testid="event-starts-weekday"
                              >
                                {startsHint}
                              </div>
                            )}
                          </div>
                          {showEndsAt ? (
                            <div>
                              <label className="label-tiny block mb-2">
                                {t({ en: "Ends (optional)", da: "Slutter (valgfrit)" })}
                              </label>
                              <input
                                type={isAllDay ? "date" : "datetime-local"}
                                value={
                                  isAllDay
                                    ? (draft.ends_at || "").slice(0, 10)
                                    : draft.ends_at
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setDraft({
                                    ...draft,
                                    ends_at: isAllDay && v ? `${v}T00:00` : v,
                                  });
                                }}
                                className="ff-input tabular"
                                data-testid="event-ends-at"
                              />
                              {endsHint && (
                                <div
                                  className="label-tiny mt-1 text-[color:var(--ink-3)]"
                                  data-testid="event-ends-weekday"
                                >
                                  {endsHint}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => {
                                  setExpandedSections((s) => ({ ...s, endsAt: true }));
                                  recordSectionExpand("endsAt");
                                }}
                                className="ff-chip ff-chip-sm inline-flex items-center gap-1.5"
                                data-testid="event-add-ends-chip"
                              >
                                <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                                {t({ en: "Ends time", da: "Slut-tidspunkt" })}
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* Recurrence picker — disabled when editing a virtual
                      instance's "this only" override. For master events
                      and new events it sets/clears the rrule. */}
                  {!draft._isInstance && (
                    <RecurrencePicker
                      value={draft.rrule}
                      onChange={(rr) => setDraft((d) => ({ ...d, rrule: rr }))}
                    />
                  )}
                  {draft._isInstance && draft.rrule && (
                    <div
                      className="text-[12px] text-[color:var(--ink-3)] flex items-center gap-1.5"
                      data-testid="event-instance-rrule-readonly"
                    >
                      <Repeat className="w-3.5 h-3.5" strokeWidth={2} />
                      <span>{summarizeRrule(draft.rrule) || "Repeats"} · scope picked on Save</span>
                    </div>
                  )}
                  <div>
                    <label className="label-tiny block mb-2">
                      {t({ en: "Who it's for (optional)", da: "Hvem er det for (valgfrit)" })}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {members.filter((m) => m.role !== "pet").map((m) => (
                        <button
                          key={m.id}
                          onClick={() => toggleMember(m.id)}
                          className={`ff-chip inline-flex items-center gap-1.5 ${
                            draft.member_ids.includes(m.id) ? "is-active" : ""
                          }`}
                          data-testid={`event-member-${m.id}`}
                        >
                          <MemberChipAvatar member={m} /> {m.name}
                        </button>
                      ))}
                    </div>
                    {/* Per-attendee push timing — two checkboxes per
                        tagged person:
                          • Now    → push on save
                          • On the day → 1h before timed events, or
                            07:00 local for all-day events.
                        Defaults off so we don't surprise anyone. */}
                    {draft.member_ids.length > 0 && (
                      <div className="mt-3 ff-card p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="label-tiny text-[color:var(--ink-3)] flex-1">
                            Notify
                          </div>
                          <div className="label-tiny text-[color:var(--ink-3)] w-12 text-center">Now</div>
                          <div className="label-tiny text-[color:var(--ink-3)] w-12 text-center">On day</div>
                        </div>
                        {draft.member_ids.map((mid) => {
                          const mm = members.find((m) => m.id === mid);
                          if (!mm) return null;
                          const onSave = (draft.notify_member_ids || []).includes(mid);
                          const onDay = (draft.notify_on_day_member_ids || []).includes(mid);
                          const toggle = (which, val) => {
                            setDraft((prev) => {
                              const key = which === "save" ? "notify_member_ids" : "notify_on_day_member_ids";
                              const set = new Set(prev[key] || []);
                              if (val) set.add(mid);
                              else set.delete(mid);
                              return { ...prev, [key]: Array.from(set) };
                            });
                          };
                          return (
                            <div
                              key={mid}
                              className="flex items-center gap-2 text-[13px]"
                              data-testid={`event-notify-row-${mid}`}
                            >
                              <span className="flex-1 inline-flex items-center gap-2 min-w-0">
                                <MemberChipAvatar member={mm} />
                                <span className="truncate">{mm.name}</span>
                              </span>
                              <input
                                type="checkbox"
                                checked={onSave}
                                onChange={(e) => toggle("save", e.target.checked)}
                                className="w-4 h-4 mx-auto block"
                                aria-label={`Notify ${mm.name} now`}
                                data-testid={`event-notify-now-${mid}`}
                                style={{ width: "3rem" }}
                              />
                              <input
                                type="checkbox"
                                checked={onDay}
                                onChange={(e) => toggle("day", e.target.checked)}
                                className="w-4 h-4 mx-auto block"
                                aria-label={`Notify ${mm.name} on the day`}
                                data-testid={`event-notify-day-${mid}`}
                                style={{ width: "3rem" }}
                              />
                            </div>
                          );
                        })}
                        <p className="text-[11px] text-[color:var(--ink-3)] pt-1 leading-relaxed whitespace-nowrap overflow-hidden text-ellipsis">
                          1 h before timed · 7 AM local for all-day
                        </p>
                      </div>
                    )}
                  </div>
                  {showNotes ? (
                    <div>
                      <label className="label-tiny block mb-2">
                        {t({ en: "Notes (optional)", da: "Noter (valgfrit)" })}
                      </label>
                      <textarea
                        value={draft.notes}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        placeholder="Anything to remember…"
                        maxLength={300}
                        rows={2}
                        className="ff-input"
                        data-testid="event-notes"
                      />
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedSections((s) => ({ ...s, notes: true }));
                          recordSectionExpand("notes");
                        }}
                        className="ff-chip ff-chip-sm inline-flex items-center gap-1.5"
                        data-testid="event-add-notes-chip"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {t({ en: "Add a note", da: "Tilføj en note" })}
                      </button>
                    </div>
                  )}
                  <div>
                    <label className="label-tiny block mb-2">{t({ en: "Photo (optional)", da: "Foto (valgfrit)" })}</label>
                    <EventAttachmentPicker
                      value={draft.attachment_image}
                      onChange={(v) => setDraft({ ...draft, attachment_image: v })}
                      tier={tier}
                      onUpgradeClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("famfeel:open-billing", {
                            detail: { reason: { suggested_tier: "plus", title: "Photo attachments are part of Plus", message: "Tickets, flyers and receipts attach to events on the Plus plan and above." } },
                          })
                        )
                      }
                    />
                    <p className="text-[11px] text-[color:var(--ink-3)] mt-1.5 leading-relaxed">
                      Concert ticket, school flyer, vet receipt — anything you'd want at hand on the day.
                    </p>
                  </div>
                  {houses.length > 0 && (
                    <div>
                      <label className="label-tiny block mb-2">
                        House (optional — leave blank for family-wide)
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => setDraft({ ...draft, house_id: "" })}
                          className={`ff-chip ${draft.house_id === "" ? "is-active" : ""}`}
                          data-testid="event-house-none"
                        >
                          Family-wide
                        </button>
                        {houses.map((h) => (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => setDraft({ ...draft, house_id: h.id })}
                            className={`ff-chip ${draft.house_id === h.id ? "is-active" : ""}`}
                            data-testid={`event-house-${h.id}`}
                          >
                            {h.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Linked list picker — pick an existing list to attach
                      OR type a new title and we'll create + link it on save.
                      Both paths share the same `linked_list_id` resolution
                      in `save()`. Collapsed by default — chip below
                      expands it. */}
                  {showLinkedList ? (
                    <div>
                      <label className="label-tiny block mb-2">
                        Linked list (optional)
                      </label>
                      {draft.linked_list_id ? (
                        // Already linked — show the current list as a pill
                        // with an unlink button. Switching lists requires
                        // unlinking first; keeps the UI predictable.
                        (() => {
                          const linked = todoLists.find((l) => l.id === draft.linked_list_id);
                          return (
                            <div className="flex items-center gap-2">
                              <span
                                className="ff-chip is-active"
                                data-testid="event-linked-list-active"
                              >
                                📝 {linked ? linked.title : "Linked list"}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setDraft({
                                    ...draft,
                                    linked_list_id: "",
                                    linked_list_new_title: "",
                                  })
                                }
                                className="ff-btn text-sm h-8"
                                data-testid="event-linked-list-unlink"
                              >
                                Unlink
                              </button>
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          <select
                            value=""
                            onChange={(e) => {
                              const id = e.target.value;
                              if (!id) return;
                              setDraft({
                                ...draft,
                                linked_list_id: id,
                                linked_list_new_title: "",
                              });
                            }}
                            className="ff-input mb-2"
                            data-testid="event-linked-list-picker"
                          >
                            <option value="">Choose an existing list…</option>
                            {todoLists.map((l) => (
                              <option key={l.id} value={l.id}>
                                {l.title}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={draft.linked_list_new_title}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                linked_list_new_title: e.target.value,
                              })
                            }
                            placeholder={
                              draft.title.trim()
                                ? `…or new list named "${draft.title.trim()}"`
                                : "…or type a name to create a new list"
                            }
                            maxLength={60}
                            className="ff-input"
                            data-testid="event-linked-list-new"
                          />
                          <p className="text-[11px] text-[color:var(--ink-3)] mt-1.5 leading-relaxed">
                            Pin a packing list, shopping list or anything else to
                            this event so the family knows what's for what.
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedSections((s) => ({ ...s, linkedList: true }));
                          recordSectionExpand("linkedList");
                        }}
                        className="ff-chip ff-chip-sm inline-flex items-center gap-1.5"
                        data-testid="event-add-linked-list-chip"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {t({ en: "Link a list", da: "Link en liste" })}
                      </button>
                    </div>
                  )}
                  {/* Inline conflict warning — calmer alternative to a
                      toast. When the soft-conflict check finds an
                      overlap with someone else's event, list the human
                      names + times here. The Save button below
                      relabels to "Add anyway" so a parent can confirm
                      with the same gesture instead of a second prompt. */}
                  {conflictWarning && conflictWarning.conflicts && conflictWarning.conflicts.length > 0 && (
                    <div
                      className="rounded-[10px] border border-[color:var(--line)] px-4 py-3 space-y-1.5"
                      style={{ background: "#ECD5F5" }}
                      role="status"
                      data-testid="event-conflict-warning"
                    >
                      <div className="label-tiny text-[color:var(--ink-3)]">
                        Heads up —
                      </div>
                      {conflictWarning.conflicts.slice(0, 3).map((c) => {
                        const names = (c.clash_member_ids || [])
                          .map((mid) => members.find((m) => m.id === mid)?.name)
                          .filter(Boolean);
                        const phrase =
                          names.length === 0
                            ? "someone"
                            : names.length === 1
                              ? names[0]
                              : names.length === 2
                                ? `${names[0]} & ${names[1]}`
                                : `${names.slice(0, -1).join(", ")} & ${names.slice(-1)[0]}`;
                        let when = "";
                        try {
                          // Use the family's chosen time format. Drop
                          // ":00" only for 12h ("3:00pm" → "3pm") since
                          // 24h ("16:00" → "16") would be ambiguous.
                          const fmtCompact = (iso) => {
                            const raw = fmtTime(iso).toLowerCase();
                            return raw.includes("am") || raw.includes("pm")
                              ? raw.replace(":00", "")
                              : raw;
                          };
                          const startStr = fmtCompact(c.starts_at);
                          // Include the end time if it's a different
                          // clock value than the start — same-instant
                          // events read cleaner without "14:00–14:00".
                          const endStr =
                            c.ends_at && c.ends_at !== c.starts_at
                              ? fmtCompact(c.ends_at)
                              : "";
                          when = endStr ? `${startStr}–${endStr}` : startStr;
                        } catch { /* leave blank */ }
                        return (
                          <div
                            key={c.id}
                            className="text-[14px] leading-snug text-[color:var(--ink)]"
                            data-testid={`event-conflict-row-${c.id}`}
                          >
                            {phrase} {names.length === 1 ? "has" : "have"} <span className="font-display-italic">{c.title || "another event"}</span>
                            {when ? ` at ${when}` : ""}.
                          </div>
                        );
                      })}
                      {conflictWarning.conflicts.length > 3 && (
                        <div className="label-tiny text-[color:var(--ink-3)]">
                          +{conflictWarning.conflicts.length - 3} more
                        </div>
                      )}
                      <div className="label-tiny text-[color:var(--ink-3)] pt-0.5">
                        {t({
                          en: "Tap \u201cAdd anyway\u201d to keep this. Edit the time or people above to recheck.",
                          da: "Tryk på \u201cTilføj alligevel\u201d for at beholde. Rediger tid eller personer ovenfor for at tjekke igen.",
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 justify-between flex-wrap">
                    <button
                      type="button"
                      onClick={saveAsTemplate}
                      disabled={busy || !draft.title.trim()}
                      className="text-[11px] uppercase tracking-[0.06em] text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 self-center"
                      data-testid="event-save-as-template"
                      title={t({ en: "Save these settings as a reusable template", da: "Gem indstillinger som en genbrugelig skabelon" })}
                    >
                      {t({ en: "⭐ Save as template", da: "⭐ Gem som skabelon" })}
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setAddOpen(false); setConflictWarning(null); }}
                        className="ff-btn"
                        data-testid="event-cancel"
                      >
                        {t({ en: "Cancel", da: "Annullér" })}
                      </button>
                      <button
                        onClick={save}
                        disabled={busy}
                        className="ff-btn ff-btn-primary"
                        data-testid="event-save"
                      >
                        {busy
                          ? t({ en: "Saving…", da: "Gemmer…" })
                          : conflictWarning
                            ? t({ en: "Add anyway", da: "Tilføj alligevel" })
                            : editingId
                              ? t({ en: "Save changes", da: "Gem ændringer" })
                              : t({ en: "Add event", da: "Tilføj begivenhed" })}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upcoming */}
          <section>
            {events !== null && (
              <>
                <MonthGrid
                  events={merged || []}
                  members={members}
                  fadeTint={fadeTint}
                  isParent={isParent}
                  canAdd={isParent}
                  selectedDay={selectedDay}
                  onSelectDay={selectDayAndScroll}
                  onAddForDate={openAddForDate}
                  onOpenEvent={(ev) => {
                    if (ev.source === "birthday") return;
                    if (isParent) openEdit(ev);
                  }}
                  onPatchEvent={patchEventDrag}
                  onCreateAt={({ dateKey, hour, minute }) => {
                    if (!isParent) return;
                    const [y, m, d] = dateKey.split("-").map(Number);
                    const at = new Date(y, m - 1, d, hour || 9, minute || 0, 0, 0);
                    const end = new Date(at.getTime() + 60 * 60_000);
                    setEditingId(null);
                    setDraft({
                      ...emptyDraft,
                      starts_at: isoToDatetimeLocal(at.toISOString()),
                      ends_at: isoToDatetimeLocal(end.toISOString()),
                    });
                    setAddOpen(true);
                  }}
                  onDropEvent={(eventId, dropDateKey) => {
                    const ev = (merged || []).find((x) => x.id === eventId);
                    if (ev) moveEventToDate(ev, dropDateKey);
                  }}
                />
                {/* View toggle — Day / All upcoming. The Month/Week
                    grid lives inside MonthGrid above; this lower row
                    only switches between today's-events and the full
                    chronological list. */}
                <div
                  ref={eventsAnchorRef}
                  className="grid grid-cols-2 gap-1.5 mb-3 scroll-mt-4"
                >
                  <button
                    type="button"
                    onClick={() => {
                      // Tap → switch to day view; if we're already in
                      // day view, jump back to today. Restores the
                      // "Today" jump shortcut some users had relied on.
                      const t = new Date();
                      const todayKey = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
                      if (calendarView === "day" && selectedDay !== todayKey) {
                        setSelectedDay(todayKey);
                      } else {
                        setCalendarView("day");
                      }
                    }}
                    className={`ff-chip w-full justify-center text-center ${calendarView === "day" ? "is-active" : ""}`}
                    data-testid="calendar-view-day"
                    title={t({ en: "Today", da: "I dag" })}
                  >
                    {t({ en: "Today", da: "I dag" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCalendarView("list")}
                    className={`ff-chip w-full justify-center text-center ${calendarView === "list" ? "is-active" : ""}`}
                    data-testid="calendar-view-list"
                  >
                    {t({ en: "All upcoming", da: "Alle kommende" })}
                  </button>
                </div>
                {/* Day switcher — prev/next-day arrows + a tappable "today"
                    label. Sits directly under the Today / All upcoming
                    chips so it reads as the natural pagination for the
                    Today view. Hidden when "All upcoming" is selected
                    since a list across days doesn't need day navigation. */}
                {calendarView === "day" && (() => {
                  const sel = new Date(selectedDay + "T00:00:00");
                  const prev = new Date(sel); prev.setDate(prev.getDate() - 1);
                  const next = new Date(sel); next.setDate(next.getDate() + 1);
                  const fmtKey = (d) =>
                    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  // Local name intentionally NOT `t` — that identifier is
                  // reserved for the i18n translator destructured at the
                  // top of this component. Shadowing it with a Date here
                  // makes `t({ en: ..., da: ... })` calls below throw
                  // "t is not a function" and unmount the whole tree.
                  const now = new Date();
                  const todayKey = fmtKey(now);
                  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
                  const tomorrowKey = fmtKey(tomorrow);
                  let dateLabel;
                  if (selectedDay === todayKey) dateLabel = t({ en: "Today", da: "I dag" });
                  else if (selectedDay === tomorrowKey) dateLabel = t({ en: "Tomorrow", da: "I morgen" });
                  else dateLabel = sel.toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { weekday: "long", day: "numeric", month: "long" });
                  const subLabel = sel.toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
                  const sameLabel = dateLabel === subLabel;
                  return (
                    <div
                      className="flex items-center justify-between gap-4 mb-4 px-2"
                      data-testid="day-switcher"
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedDay(fmtKey(prev))}
                        className="ff-chip !px-3.5 !py-1.5"
                        aria-label="Previous day"
                        data-testid="day-switcher-prev"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedDay(todayKey)}
                        className="flex-1 text-center min-w-0 group px-2"
                        title="Tap to jump to today"
                        data-testid="day-switcher-label"
                      >
                        <div className="font-display text-lg leading-tight tracking-tight truncate group-hover:text-[color:var(--accent)] transition-colors">
                          {dateLabel}
                        </div>
                        {!sameLabel && (
                          <div className="label-tiny text-[color:var(--ink-3)] mt-0.5 truncate">
                            {subLabel}
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedDay(fmtKey(next))}
                        className="ff-chip !px-3.5 !py-1.5"
                        aria-label="Next day"
                        data-testid="day-switcher-next"
                      >
                        ›
                      </button>
                    </div>
                  );
                })()}
              </>
            )}

            {/* Filters */}
            {events !== null && (events.length > 0 || members.length > 0) && (() => {
              // Compose a one-line label for the collapsed Filter chip
              // so families can tell at a glance what view they're on.
              const memberLabel = memberFilter === "all"
                ? t({ en: "Everyone", da: "Alle" })
                : (members.find((m) => m.id === memberFilter)?.name || t({ en: "Everyone", da: "Alle" }));
              const kindLabelText = kindFilter === "all"
                ? t({ en: "All kinds", da: "Alle typer" })
                : (kindLabel(kindFilter, lang) || t({ en: "All kinds", da: "Alle typer" }));
              const isActive = memberFilter !== "all" || kindFilter !== "all" || houseFilter !== "all";
              return (
                <div className="mb-3" data-testid="calendar-filters">
                  <button
                    type="button"
                    onClick={() => setFiltersOpen((v) => !v)}
                    className={`ff-chip w-full justify-between text-left ${isActive ? "is-active" : ""}`}
                    data-testid="calendar-filters-toggle"
                    aria-expanded={filtersOpen}
                  >
                    <span className="truncate">
                      <span className="label-tiny text-[color:var(--ink-3)] mr-1.5">
                        {t({ en: "Filter", da: "Filtrér" })}
                      </span>
                      {memberLabel} · {kindLabelText.toLowerCase()}
                    </span>
                    <ChevronRight
                      className={`w-3.5 h-3.5 ml-2 transition-transform ${filtersOpen ? "rotate-90" : ""}`}
                      strokeWidth={2}
                    />
                  </button>
                  {filtersOpen && (
                    <div
                      className="mt-2 ff-card p-3 space-y-2"
                      data-testid="calendar-filters-panel"
                    >
                      {members.length > 0 && (
                        <select
                          value={memberFilter}
                          onChange={(e) => setMemberFilter(e.target.value)}
                          className="ff-input py-1.5 text-[13px] w-full"
                          data-testid="calendar-filter-member"
                          aria-label="Filter by who"
                        >
                          <option value="all">{t({ en: "Everyone", da: "Alle" })}</option>
                          {members.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.avatar_emoji} {m.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <select
                        value={kindFilter}
                        onChange={(e) => setKindFilter(e.target.value)}
                        className="ff-input py-1.5 text-[13px] w-full"
                        data-testid="calendar-filter-kind"
                        aria-label="Filter by kind"
                      >
                        <option value="all">All kinds</option>
                        {kindChoices.map((k) => (
                          <option key={k.key} value={k.key}>
                            {k.emoji} {k.label}
                          </option>
                        ))}
                      </select>
                      {houses.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="calendar-house-filter">
                          <span className="label-tiny text-[color:var(--ink-3)] mr-1">House</span>
                          <button
                            type="button"
                            onClick={() => setHouseFilter("all")}
                            className={`ff-chip ${houseFilter === "all" ? "is-active" : ""}`}
                            data-testid="calendar-filter-all"
                          >
                            All
                          </button>
                          <button
                            type="button"
                            onClick={() => setHouseFilter("none")}
                            className={`ff-chip ${houseFilter === "none" ? "is-active" : ""}`}
                            data-testid="calendar-filter-none"
                          >
                            Family-wide
                          </button>
                          {houses.map((h) => (
                            <button
                              key={h.id}
                              type="button"
                              onClick={() => setHouseFilter(h.id)}
                              className={`ff-chip ${houseFilter === h.id ? "is-active" : ""}`}
                              data-testid={`calendar-filter-${h.id}`}
                            >
                              {h.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {isActive && (
                        <button
                          type="button"
                          onClick={() => {
                            setMemberFilter("all");
                            setKindFilter("all");
                            setHouseFilter("all");
                          }}
                          className="text-[11px] uppercase tracking-[0.06em] text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                          data-testid="calendar-filters-clear"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {events === null ? (
              <div className="label-tiny text-[color:var(--ink-3)]">
                {t({ en: "Loading…", da: "Indlæser…" })}
              </div>
            ) : calendarView === "week" ? (
              // Legacy state — old "Week" toggle removed, but if a
              // user's localStorage still has "week", fall back to the
              // day view. The upper Month/Week grid handles week-level
              // browsing now.
              (() => { setTimeout(() => setCalendarView("day"), 0); return null; })()
            ) : (() => {
              // In day mode, narrow the rendered groups to just the
              // selected day. We also pull in any multi-day events
              // that *span* the selected day (e.g. a Jul 1–10 trip
              // clicked on Jul 9 should still appear) — without this,
              // the month-grid bar would colour the cell while the
              // list said "Nothing on this day".
              const dayFiltered = (() => {
                if (calendarView !== "day") return upcomingGroups;
                // Past days were previously empty here because we only
                // looked at upcomingGroups (which excludes anything <
                // todayKey). Look at both pools so clicking back on
                // last Tuesday in the month grid shows last Tuesday's
                // events in the day-view list.
                const pool = selectedDay < todayKey ? pastGroups : upcomingGroups;
                const own = pool.find((g) => g.date === selectedDay);
                const sel = new Date(selectedDay + "T00:00:00");
                const selEnd = new Date(sel);
                selEnd.setHours(23, 59, 59, 999);
                const spanItems = [];
                // Multi-day events can span across the today boundary
                // (e.g. a Jun 28 – Jul 4 trip is "past" in pastGroups
                // by start-date but still spans Jul 2 if you clicked
                // today's date). So scan both pools for spans regardless
                // of which one owns the selected day.
                for (const g of [...upcomingGroups, ...pastGroups]) {
                  if (g.date === selectedDay) continue;
                  for (const ev of g.items) {
                    if (!ev.ends_at) continue;
                    const start = new Date(ev.starts_at);
                    const end = new Date(ev.ends_at);
                    // Inclusive on both ends. Single-day events fall
                    // through (their start day already matched above).
                    if (start <= selEnd && end >= sel && start.toDateString() !== sel.toDateString()) {
                      spanItems.push(ev);
                    }
                  }
                }
                if (!own && spanItems.length === 0) return [];
                // Dedupe by id — a span event could appear in both
                // pools if our scan pulled it twice (defensive; the
                // group iteration above guards against it but pasting
                // here keeps the contract obvious).
                const seen = new Set();
                const items = [...(own?.items || []), ...spanItems]
                  .filter((ev) => {
                    if (!ev.id) return true;
                    if (seen.has(ev.id)) return false;
                    seen.add(ev.id);
                    return true;
                  })
                  .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
                return [{ date: selectedDay, items }];
              })();
              const filtered = dayFiltered;
              if (filtered.length === 0) {
                if (calendarView === "day") {
                  // Compute the next-14-days preview ONCE so the empty
                  // state can show what's coming up immediately after
                  // selectedDay. Hides days that are themselves empty
                  // and stops at "Earlier · last 25" cutoff.
                  const sel = new Date(selectedDay + "T00:00:00");
                  const horizon = new Date(sel);
                  horizon.setDate(horizon.getDate() + 14);
                  const peekGroups = upcomingGroups.filter((g) => {
                    const d = new Date(g.date + "T00:00:00");
                    return d > sel && d <= horizon;
                  });
                  return (
                    <>
                      {/* Next-7-days peek — gives an empty day some
                          context. Same DayGroup component as the main
                          list so styling and stickyness stays consistent.
                          The "Nothing on this day" row was removed —
                          the top "+ Add event" button + tappable
                          calendar cells already cover the add path,
                          and the Coming-up peek does the rest. */}
                      {peekGroups.length > 0 && (
                        <div
                          className="space-y-3 pt-2"
                          data-testid="calendar-next-14-peek"
                        >
                          <div className="label-tiny text-[color:var(--ink-3)]">
                            {t({ en: "Coming up · next 14 days", da: "Kommende de næste 14 dage" })}
                          </div>
                          {peekGroups.map((g) => (
                            <DayGroup
                              key={g.date}
                              dateKey={g.date}
                              items={g.items}
                              members={members}
                              isParent={isParent}
                              onEdit={openEdit}
                              onDelete={remove}
                              onDropEvent={(eventId, dropDateKey) => {
                                const ev = (merged || []).find((x) => x.id === eventId);
                                if (ev) moveEventToDate(ev, dropDateKey);
                              }}
                              fadeTint={fadeTint}
                              smallAvatars={smallAvatars}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  );
                }
                return (
                  <div className="label-tiny text-[color:var(--ink-3)]">
                    {t({ en: "Nothing on the calendar yet.", da: "Ingenting på kalenderen endnu." })}
                  </div>
                );
              }
              if (calendarView === "day" && filtered.length > 0) {
                // Day view list — uses the day-switcher header rendered
                // above the filter; no pagination chrome inline here.
                return (
                  <>
                    {filtered.map((g) => (
                      <DayGroup
                        key={g.date}
                        dateKey={g.date}
                        items={g.items}
                        members={members}
                        isParent={isParent}
                        onEdit={openEdit}
                        onDelete={remove}
                        onDropEvent={(eventId, dropDateKey) => {
                          const ev = (merged || []).find((x) => x.id === eventId);
                          if (ev) moveEventToDate(ev, dropDateKey);
                        }}
                      fadeTint={fadeTint}
                      smallAvatars={smallAvatars}
                    />
                  ))}
                </>
              );
              }
              // List ("All upcoming") view — no day-switcher, no toggle.
              return (
                <>
                  {filtered.map((g) => (
                    <DayGroup
                      key={g.date}
                      dateKey={g.date}
                      items={g.items}
                      members={members}
                      isParent={isParent}
                      onEdit={openEdit}
                      onDelete={remove}
                      onDropEvent={(eventId, dropDateKey) => {
                        const ev = (merged || []).find((x) => x.id === eventId);
                        if (ev) moveEventToDate(ev, dropDateKey);
                      }}
                      fadeTint={fadeTint}
                      smallAvatars={smallAvatars}
                    />
                  ))}
                </>
              );
            })()}
          </section>

          {/* Past — compact single-line list. Capped at 25 so the
              drawer stays scannable; items beyond 25 get hard-deleted
              from the server below so storage doesn't bloat either.
              Collapsed by default — past events are clutter on the
              way to today's tiles, so we only show the count and an
              opt-in "Show" reveal. Tapping expands the full set in
              one go (everyone's instinct after seeing the count). */}
          {pastGroups.length > 0 && (() => {
            const allPast = pastGroups.flatMap((g) => g.items).slice(0, 25);
            return (
              <section>
                <button
                  type="button"
                  onClick={() => setPastExpanded((v) => !v)}
                  className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)] inline-flex items-center gap-1.5"
                  data-testid="past-events-toggle"
                >
                  Earlier · last {allPast.length}
                  <span aria-hidden="true">{pastExpanded ? "▾" : "▸"}</span>
                </button>
                {pastExpanded && (
                  <ul
                    className="mt-3 divide-y divide-[color:var(--line-soft)]"
                    data-testid="past-events-compact"
                  >
                    {allPast.map((ev) => (
                      <PastRow
                        key={ev.id}
                        event={ev}
                        members={members}
                        isParent={isParent}
                        onEdit={openEdit}
                        onDelete={remove}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })()}
        </div>
  );

  // Inline mode (calendar dashboard) — return content directly.
  if (inline) {
    return (
      <CalendarPrefsContext.Provider value={{ colorCodeByMember }}>
      <div data-testid="calendar-board">
        {content}
        {scopePrompt && (
          <ScopePickerDialog
            kind={scopePrompt.kind}
            onCancel={() => setScopePrompt(null)}
            onPick={confirmScope}
          />
        )}
        {showScanFlyer && (
          <ScanFlyerDialog
            members={members}
            hasBYOKey={hasBYOKey}
            onOpenSettings={onOpenSettings}
            onCreated={async () => {
              // Reload the events list so the freshly-inserted rows
              // pop into the calendar without needing a manual refresh.
              try { await load(); } catch {}
            }}
            onClose={() => setShowScanFlyer(false)}
          />
        )}
      </div>
      </CalendarPrefsContext.Provider>
    );
  }

  // Modal mode (mood / pets) — wrap with the full overlay shell.
  return (
    <CalendarPrefsContext.Provider value={{ colorCodeByMember }}>
    <div
      className="fixed inset-0 z-40 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="calendar-sheet"
    >
      <div
        className="relative bg-[color:var(--bg)] md:rounded-[20px] w-full md:max-w-3xl max-h-[100vh] md:max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] px-5 md:px-7 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div>
            <div className="label-tiny">Family calendar</div>
            <h2 className="font-display-italic text-3xl md:text-4xl leading-none mt-1 tracking-tight">
              What’s coming up
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            data-testid="calendar-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        {content}
      </div>
      {scopePrompt && (
        <ScopePickerDialog
          kind={scopePrompt.kind}
          onCancel={() => setScopePrompt(null)}
          onPick={confirmScope}
        />
      )}
      {showScanFlyer && (
        <ScanFlyerDialog
          members={members}
          hasBYOKey={hasBYOKey}
          onOpenSettings={onOpenSettings}
          onCreated={async () => {
            try { await load(); } catch {}
          }}
          onClose={() => setShowScanFlyer(false)}
        />
      )}
    </div>
    </CalendarPrefsContext.Provider>
  );
}

// Anchor a Date to the Monday of its week (00:00 local).
// (Re-exported here for convenience even though WeekViewGrid also exports it.)

function WeekViewSection({ weekAnchor, setWeekAnchor, events, members, fadeTint, isParent, onSelect, onCreate, onPatch }) {
  const shiftWeek = (delta) => {
    const next = new Date(weekAnchor);
    next.setDate(weekAnchor.getDate() + delta * 7);
    setWeekAnchor(next);
  };
  const endOfWeek = new Date(weekAnchor);
  endOfWeek.setDate(weekAnchor.getDate() + 6);
  const fmt = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const today = new Date();
  const isThisWeek = today >= weekAnchor && today <= new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate(), 23, 59);
  return (
    <div className="space-y-3" data-testid="calendar-week-view">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => shiftWeek(-1)}
          className="ff-btn h-8 px-2"
          aria-label="Previous week"
          data-testid="week-prev"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="text-center">
          <div className="font-display text-[15px] tracking-tight">
            {fmt(weekAnchor)} — {fmt(endOfWeek)}
          </div>
          {!isThisWeek && (
            <button
              type="button"
              onClick={() => setWeekAnchor(weekStartFromDate(new Date()))}
              className="text-[10px] uppercase tracking-[0.06em] mt-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
              data-testid="week-jump-today"
            >
              Jump to this week
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => shiftWeek(1)}
          className="ff-btn h-8 px-2"
          aria-label="Next week"
          data-testid="week-next"
        >
          <ChevronRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
      <WeekViewGrid
        weekStart={weekAnchor}
        events={events}
        members={members}
        fadeTint={fadeTint}
        isParent={isParent}
        onSelect={onSelect}
        onCreate={onCreate}
        onPatch={onPatch}
      />
      <p className="text-[11px] text-[color:var(--ink-3)] leading-relaxed text-center">
        Tap an empty slot to add. Drag an event to move it. Drag the bottom edge to stretch.
      </p>
    </div>
  );
}


function PastRow({ event, members, isParent, onEdit, onDelete }) {
  const kind = kindOf(event.kind);
  const isVirtual = event.source === "birthday";
  const isBirthdayEvent = isVirtual && event.kind === "birthday";
  const attendees = (event.member_ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter((m) => m && (isBirthdayEvent ? true : m.role !== "pet"));
  const when = new Date(event.starts_at);
  const whenLabel = isNaN(when)
    ? ""
    : when.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
  return (
    <li
      className="flex items-center gap-2 py-2 text-[13px] text-[color:var(--ink-2)] hover:text-[color:var(--ink)] transition-colors group"
      data-testid={`past-event-${event.id}`}
    >
      <span
        className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] leading-none"
        style={{ background: kind.tint }}
        aria-hidden="true"
      >
        {eventEmoji(event)}
      </span>
      <span className="label-tiny text-[color:var(--ink-3)] tabular w-12 flex-shrink-0">
        {whenLabel}
      </span>
      <button
        type="button"
        className="truncate text-left flex-1 disabled:cursor-default"
        onClick={() => !isVirtual && isParent && onEdit && onEdit(event)}
        disabled={isVirtual || !isParent}
        title={event.title}
      >
        {event.title}
      </button>
      {attendees.length > 0 && (
        <span className="label-tiny text-[color:var(--ink-3)] truncate max-w-[120px] hidden sm:inline">
          {attendees.slice(0, 3).map((m) => m.name.split(" ")[0]).join(", ")}
          {attendees.length > 3 ? "…" : ""}
        </span>
      )}
      {isParent && !isVirtual && onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(event.id);
          }}
          className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--rough-strong)] opacity-0 group-hover:opacity-100 md:opacity-100 transition"
          title="Delete this past event"
          aria-label={`Delete ${event.title}`}
        >
          ✕
        </button>
      )}
    </li>
  );
}


function DayGroup({ dateKey, items, members, isParent, onEdit, onDelete, onDropEvent, muted, fadeTint, smallAvatars, editingId }) {
  const { lang } = useI18n();
  const [isDropOver, setIsDropOver] = React.useState(false);
  const acceptsDrop = !!onDropEvent && isParent;
  return (
    <div
      className={`mb-3 scroll-mt-24 transition-colors ${
        isDropOver ? "bg-[color:var(--accent)]/10 rounded-xl" : ""
      }`}
      id={`ff-daygroup-${dateKey}`}
      onDragOver={(e) => {
        if (!acceptsDrop) return;
        if (e.dataTransfer && e.dataTransfer.types.includes("application/x-famfeel-event")) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setIsDropOver(true);
        }
      }}
      onDragLeave={() => setIsDropOver(false)}
      onDrop={(e) => {
        setIsDropOver(false);
        if (!acceptsDrop) return;
        const id = e.dataTransfer.getData("application/x-famfeel-event");
        if (!id) return;
        e.preventDefault();
        onDropEvent(id, dateKey);
      }}
    >
      <h3
        className={`label-tiny mb-1.5 sticky top-0 z-[1] -mx-1 px-1 py-1.5 bg-[color:var(--bg)]/95 backdrop-blur-sm border-b border-[color:var(--line-soft)] ${
          muted ? "text-[color:var(--ink-3)]" : "text-[color:var(--ink-2)]"
        } ${isDropOver ? "text-[color:var(--accent)]" : ""}`}
      >
        {dayLabel(dateKey, lang)}
        {isDropOver && (
          <span className="ml-2 text-[10px] uppercase tracking-[0.06em] text-[color:var(--accent)]">
            ← drop here
          </span>
        )}
      </h3>
      <ul className="space-y-1.5 ff-stagger">
        {items.map((ev) => (
          <EventRow
            key={ev.id}
            event={ev}
            members={members}
            isParent={isParent}
            onEdit={onEdit}
            onDelete={onDelete}
            muted={muted}
            fadeTint={fadeTint}
            smallAvatars={smallAvatars}
            isBeingEdited={editingId === ev.id}
          />
        ))}
      </ul>
    </div>
  );
}

function EventRow({ event, members, isParent, onEdit, onDelete, muted, fadeTint, smallAvatars, isBeingEdited }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { colorCodeByMember } = useContext(CalendarPrefsContext);
  // Hook must come before any early-return to keep call order stable
  // across renders (react-hooks/rules-of-hooks).
  const [burstKey, setBurstKey] = useState(0);
  // Toddler-mode-friendly wiggle on tap. Setting `shaking=true`
  // applies the keyframe class; an effect clears it after the
  // animation settles. Re-tapping mid-shake is a no-op (the class
  // is already on) — that's fine, the user gets feedback either way.
  const [shaking, setShaking] = useState(false);
  useEffect(() => {
    if (!shaking) return undefined;
    const t = setTimeout(() => setShaking(false), 360);
    return () => clearTimeout(t);
  }, [shaking]);
  // Band events (school holidays, public holidays) render as a quiet
  // single italic line so they don't compete with real family events
  // for attention. They're decorative context, not action items.
  if (event.display_as === "band") {
    return (
      <li
        className="text-[12px] text-[color:var(--ink-3)] italic px-1 py-0.5 leading-snug"
        data-testid={`event-band-${event.id}`}
      >
        ✦ {event.title || "Day off"}
      </li>
    );
  }
  const kind = kindOf(event.kind);
  const isVirtual = event.source === "birthday";
  const countdown = eventCountdown(event.starts_at, event.ends_at);
  // Pets don't attend dentist appointments — but their own birthday is
  // as real as a human's, so keep them in the attendees list when this
  // is a synthesized birthday event.
  const isBirthdayEvent = isVirtual && event.kind === "birthday";
  const attendees = (event.member_ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter((m) => m && (isBirthdayEvent ? true : m.role !== "pet"));
  const isBirthday = event.kind === "birthday";
  // Whole-row click → opens the event's detail page (same flow as
  // tapping an event in Display mode) so Edit + Trash live on the
  // dedicated event screen instead of crowding every card on the
  // dashboard. Synthesised birthday events still burst confetti and
  // stay on-card — they have no /event/:id record. Recurring
  // instances DO navigate (their state.event hydrates the page; Edit
  // there hands off to the calendar's scope picker).
  const isTappable = isBirthday || event.source !== "birthday";
  const handleRowClick = (e) => {
    setShaking(true);
    if (isBirthday) {
      setBurstKey((k) => k + 1);
      return;
    }
    if (event.source === "birthday") return;
    navigate(`/event/${encodeURIComponent(event.id)}`, {
      state: { event },
    });
  };
  return (
    <li
      className={`relative ${smallAvatars ? "px-3 py-1.5" : "px-3 py-2.5"} rounded-xl flex items-start ${smallAvatars ? "gap-2.5" : "gap-3"} ${shaking ? "ff-shake-on-tap" : ""} ${muted ? "opacity-70" : ""} ${
        isTappable ? "cursor-pointer select-none hover:brightness-[0.97]" : ""
      } ${isBeingEdited ? "ring-2 ring-[color:var(--accent)] ring-offset-1 ring-offset-[color:var(--bg)]" : ""}`}
      style={{
        // Color-code-by-member mode: card stays neutral (paper-white
        // with a 1px ink keyline so it still reads as a row), and the
        // member accents render as a vertical stripe rail down the
        // right edge. Birthday + ICS-imported special cases keep their
        // own visual signals.
        background: colorCodeByMember
          ? "var(--surface)"
          : (fadeTint
              ? `color-mix(in srgb, var(--bg) 50%, ${kind.tint})`
              : kind.tint),
        border: colorCodeByMember ? "1px solid rgba(11,11,9,0.10)" : undefined,
        // Tiny left rail for ICS-imported events. Subtle but instantly
        // tells the parent which events came from a synced calendar
        // (Google / iCloud) vs ones FamFeel owns.
        borderLeft: event.source === "ics"
          ? "3px solid var(--accent)"
          : (colorCodeByMember ? "1px solid rgba(11,11,9,0.10)" : undefined),
        // Reserve right padding so content doesn't sit under the
        // accent rail. 24px stripe + 6px breathing = 30px.
        paddingRight: colorCodeByMember ? "30px" : undefined,
      }}
      onClick={handleRowClick}
      draggable={isParent && !isVirtual}
      onDragStart={(e) => {
        if (!isParent || isVirtual) return;
        e.dataTransfer.setData("application/x-famfeel-event", event.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      data-testid={`event-row-${event.id}`}
    >
      {/* Member-accent stripe rail (right edge). Each assigned member
          gets an equal vertical slice in their `accent_color`. Skipped
          when the feature is off or when nobody's tagged on the event
          (an event without attendees gets no rail). Width: 24px so the
          colour reads boldly at arm's length / kitchen-display
          distance — twice the original for max scan-ability. */}
      {colorCodeByMember && attendees.length > 0 && (
        <div
          className="absolute top-0 right-0 bottom-0 flex flex-col rounded-r-xl overflow-hidden pointer-events-none"
          style={{ width: "24px" }}
          aria-hidden="true"
          data-testid={`event-row-accents-${event.id}`}
        >
          {attendees.slice(0, 6).map((m) => (
            <span
              key={m.id}
              className="flex-1 block"
              style={{ background: memberAccentHex(m) }}
              title={m.name}
            />
          ))}
        </div>
      )}
      {burstKey > 0 && (
        <ConfettiBurst
          key={burstKey}
          onDone={() => {/* piece auto-unmounts via internal timer */}}
        />
      )}
      {/* Member-birthday events show 🎉 on the left and the
          birthday person's avatar on the right (in the attendees
          row below). Two avatars of the same person — one tiny on
          the left, one large in the attendees strip — read as a
          duplicate; emoji on the left keeps the celebration
          signal without the doubling. */}
      <span
        className={`flex items-center justify-center flex-shrink-0 ${smallAvatars ? "w-8 h-8 text-xl" : "w-10 h-10 text-2xl"}`}
        aria-hidden="true"
      >
        {eventEmoji(event)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[15px] leading-tight tracking-tight text-[color:var(--accent)]">
          {event.title}
          {event.is_recurring_instance && (
            <span
              className="ml-1.5 align-middle inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-full bg-[color:var(--bg)]/70 text-[color:var(--ink)]"
              title="Repeating event"
              data-testid={`event-row-recurring-${event.id}`}
            >
              <Repeat className="w-3 h-3" strokeWidth={2} />
            </span>
          )}
          {event.source === "ics" && (
            <span
              className="ml-1.5 align-middle inline-block text-[color:var(--accent)] label-tiny"
              title="Imported from a connected calendar — tap to assign attendees"
              data-testid={`event-row-source-ics-${event.id}`}
            >
              · synced
            </span>
          )}
          {event.attachment_image && (
            <span
              className="ml-1.5 align-middle inline-block text-[13px] opacity-70"
              title="Photo attached — tap to view"
              data-testid={`event-row-attachment-${event.id}`}
              aria-label="Photo attached"
            >
              📎
            </span>
          )}
        </div>
        {/* Compact "time · attendees" line. Avatars used to live on
            their own row below, which doubled card height. They now
            sit inline as a small avatar strip after the time so a
            typical "TODAY · 09:00 [HC]" event collapses to two short
            lines total. While the event is in progress, an inline
            "● now · ends 14:30" suffix replaces the bulky right-rail
            pill — keeps the live signal without forcing the title to
            wrap on narrow screens. Notes (if any) still spill below
            — rare and worth the extra height when present. */}
        <div className="flex items-center gap-2 mt-0.5 text-[11px] uppercase tracking-[0.06em] text-[color:var(--ink)]">
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            {fmtEventWhen(event.starts_at, event.ends_at, lang)}
            {event.source === "ics" && attendees.length === 0 && (
              <span>· {t({ en: "tap to tag", da: "tryk for at markere" })}</span>
            )}
            {countdown === "ongoing" && (
              <span
                className="inline-flex items-center gap-1 text-[color:var(--accent)] font-semibold"
                data-testid={`event-row-now-${event.id}`}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]"
                  aria-hidden="true"
                />
                {t({ en: "now", da: "nu" })}
                {event.ends_at && (
                  <span className="lowercase tracking-normal font-normal opacity-80">
                    · ends {fmtTime(event.ends_at)}
                  </span>
                )}
              </span>
            )}
          </span>
          {attendees.length > 0 && (
            <div className="flex -space-x-1.5" data-testid={`event-row-attendees-${event.id}`}>
              {attendees.slice(0, 5).map((m) =>
                m.avatar_image ? (
                  <img
                    key={m.id}
                    src={m.avatar_image}
                    alt={m.name}
                    title={m.name}
                    className={`${smallAvatars ? "w-7 h-7" : "w-9 h-9"} rounded-full object-cover border border-[color:var(--bg)]`}
                  />
                ) : (
                  <span
                    key={m.id}
                    className={`${smallAvatars ? "w-7 h-7 text-sm" : "w-9 h-9 text-base"} rounded-full bg-[color:var(--surface)] border border-[color:var(--bg)] flex items-center justify-center`}
                    title={m.name}
                  >
                    {m.avatar_emoji}
                  </span>
                ),
              )}
              {attendees.length > 5 && (
                <span
                  className={`${smallAvatars ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-[11px]"} rounded-full bg-[color:var(--surface)] border border-[color:var(--bg)] flex items-center justify-center text-[color:var(--ink-2)]`}
                  title={`${attendees.length - 5} more`}
                >
                  +{attendees.length - 5}
                </span>
              )}
            </div>
          )}
        </div>
        {event.notes && (
          <div className="text-[13px] text-[color:var(--ink-2)] italic mt-1 line-clamp-2">
            “{event.notes}”
          </div>
        )}
        {/* Silent-merge footnote — same shape as the EventPill version
            so list view, day view and dashboard strip all read alike. */}
        {(() => {
          const otherIds = event.also_added_by_member_ids || [];
          if (!otherIds.length) return null;
          const names = otherIds
            .map((mid) => members.find((m) => m.id === mid)?.name)
            .filter(Boolean);
          if (!names.length) return null;
          const phrase =
            names.length === 1
              ? names[0]
              : names.length === 2
                ? `${names[0]} & ${names[1]}`
                : `${names.slice(0, -1).join(", ")} & ${names.slice(-1)[0]}`;
          return (
            <div
              className="text-[11px] text-[color:var(--ink-3)] italic mt-1"
              data-testid={`event-also-added-${event.id}`}
            >
              also added by {phrase}
            </div>
          );
        })()}
      </div>
      {/* Right rail: countdown chip + delete button. The "Happening
          now" pill that used to live here moved inline into the time
          line above so the title doesn't wrap on narrow screens.
          Pre-event countdowns ("in 3h", "tomorrow") still render here
          since they're tiny. */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {countdown && countdown !== "today" && countdown !== "ongoing" && (() => {
          // Imminent = starts in under 60 min (any `in N min` label
          // or `now`). Elevate to the saturated hot accent so it
          // pops among a screen of neutral cards — this is the
          // "one deliberate colour moment per screen" pattern.
          const imminent =
            countdown === "now" || /^in \d+ min$/.test(String(countdown));
          return (
            <span
              className={
                imminent
                  ? "ff-accent-hot label-tiny text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap"
                  : "label-tiny text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--bg)]/70 text-[color:var(--ink)] whitespace-nowrap"
              }
              data-testid={`calendar-countdown-${event.id}`}
            >
              {localizeCountdown(countdown, lang)}
            </span>
          );
        })()}
        {isParent && !isVirtual && (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(event);
              }}
              className="ff-btn h-6 w-6 !p-0 text-[color:var(--rough-strong)]"
              data-testid={`event-delete-${event.id}`}
              aria-label="Delete event"
            >
              <Trash2 className="w-2.5 h-2.5" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}


/**
 * MonthGrid — tiny 7×5 calendar shown at the top of the sheet so the
 * family can see at a glance which days are already occupied.
 *
 * Design notes:
 *  - Filters in the parent already apply to `events`, so the dots here
 *    reflect exactly what the list below shows.
 *  - Today is always in-bounds; the month defaults to today's month and
 *    the user can page forwards/backwards with the chevrons.
 *  - Event count per day is capped at 3 visible dots to stay tidy; the
 *    actual count is in the cell's aria-label and tooltip.
 *  - Clicking a day with events scrolls the matching DayGroup into view.
 *    Empty days are still clickable and do nothing (cheap + obvious).
 */
function MonthGrid({
  events, members, fadeTint, isParent,
  onSelectDay, onAddForDate, canAdd, selectedDay, onDropEvent,
  onOpenEvent, onPatchEvent, onCreateAt,
}) {
  const today = new Date();
  const [anchor, setAnchor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  // Bucket events per day, capturing the dominant kind so the cell can
  // show a small kind-tint badge instead of dots. We pick the kind with
  // the highest count for that day; ties prefer kinds with stronger
  // visual identity (birthday > school_event > activity > appointment > ...).
  const dayInfo = useMemo(() => {
    const map = new Map(); // YYYY-MM-DD → { count, kindCounts: Map, dominant: tint, isMultiDay: bool, eventIds: Set }
    // Keys MUST match EVENT_KINDS in lib/events.js. Order = tie-breaker
    // priority: a day with 1 birthday + 1 dinner shows as birthday; a
    // day with 1 dinner + 1 activity shows as activity (sportier).
    const KIND_PRIORITY = [
      "birthday",
      "school_event",
      "activity",
      "work",
      "appointment",
      "travel",
      "day_trip",
      "event",
      "dinner",
      "other",
    ];
    for (const ev of events) {
      const start = new Date(ev.starts_at);
      if (isNaN(start.getTime())) continue;
      const end = ev.ends_at ? new Date(ev.ends_at) : start;
      const cursor = new Date(start);
      cursor.setHours(0, 0, 0, 0);
      const lastDay = new Date(end);
      lastDay.setHours(0, 0, 0, 0);
      // Background-band events (school holidays, public holidays from
      // imported subscriptions) paint a 2px underline across every
      // day they span. Tracked separately so they DON'T contribute to
      // the dominant-kind tint or the count badge — those should stay
      // about the family's actual events.
      const isBand = ev.display_as === "band";
      const isMulti = lastDay > cursor;
      let safety = 0;
      while (cursor <= lastDay && safety < 60) {
        const k = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
        if (!map.has(k)) {
          map.set(k, {
            count: 0,
            kindCounts: {},
            isMultiDay: false,
            eventIds: new Set(),
            bands: [],
            hasAttachment: false,
            // Up to a few real-event titles per day so the month grid
            // can show "Football, Dentist, Mum's late shift" on tap.
            // Capped at 5 inside the title-attr build below — busy
            // days don't need a wall of text.
            titles: [],
          });
        }
        const entry = map.get(k);
        if (isBand) {
          entry.bands.push({
            id: ev.id,
            title: ev.title,
            kind: ev.kind || "other",
          });
        } else {
          entry.count += 1;
          entry.titles.push({
            title: ev.title || "Untitled",
            startsAt: ev.starts_at,
            endsAt: ev.ends_at,
            kind: ev.kind || "other",
          });
          const kk = ev.kind || "other";
          entry.kindCounts[kk] = (entry.kindCounts[kk] || 0) + 1;
          if (ev.attachment_image) {
            entry.hasAttachment = true;
          }
          if (isMulti) {
            entry.isMultiDay = true;
            // Only multi-day events extend a tint bar across cells.
            // Single-day events live on one cell only and shouldn't
            // bridge into neighbours even if the neighbour has its own
            // event on the same day.
            entry.eventIds.add(ev.id);
          }
        }
        cursor.setDate(cursor.getDate() + 1);
        safety += 1;
      }
    }
    // Resolve dominant + secondary kind per day → tint colours. The
    // secondary appears as the bottom half of the day-cell when ≥2
    // distinct kinds occupy the same day, so families can see at a
    // glance "two different things going on" without us showing dots.
    // In teen-plus families we still soften the tints, but the
    // month grid is the densest preview surface in the app — pinch
    // 50% of the paper out and the dots disappear into near-white.
    // Use a much lighter mix (20% paper) here so the kind palette
    // stays readable, even though we still mix 50% on event rows
    // and chips below where each tint already has more area.
    const fade = (c) => (fadeTint ? `color-mix(in srgb, var(--bg) 20%, ${c})` : c);
    for (const [, entry] of map) {
      const ranked = Object.entries(entry.kindCounts)
        .filter(([, c]) => c > 0)
        .sort((a, b) => {
          if (b[1] !== a[1]) return b[1] - a[1];
          return KIND_PRIORITY.indexOf(a[0]) - KIND_PRIORITY.indexOf(b[0]);
        });
      const top = ranked[0]?.[0] || "other";
      const second = ranked[1]?.[0] || null;
      entry.dominantKind = top;
      entry.tint = fade(kindOf(top).tint);
      entry.secondaryKind = second;
      entry.secondaryTint = second ? fade(kindOf(second).tint) : null;
    }
    return map;
  }, [events, fadeTint]);

  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const monthLabel = anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  // Build a 6-week grid, week-starting-Monday. Calendars that start on
  // Sunday are mostly a US convention; FamFeel families skew EU + "the
  // week starts when school does", so Monday-first is the kinder default.
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // getDay: 0=Sun..6=Sat. Convert to Mon=0..Sun=6.
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const monthCells = [];
  for (let i = 0; i < startOffset; i++) monthCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) monthCells.push({ day: d, date: new Date(year, month, d) });
  while (monthCells.length % 7 !== 0) monthCells.push(null);

  const cells = monthCells;

  const isToday = (date) =>
    date && date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const keyFor = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

  const shiftMonth = (delta) =>
    setAnchor(new Date(year, month + delta, 1));

  return (
    <div
      className="mb-3 rounded-xl border border-[color:var(--line-soft)] bg-[color:var(--surface)] p-2.5 md:p-3"
      data-testid="calendar-month-grid"
    >
      <div className="flex items-center justify-between mb-1.5">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="w-7 h-7 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center text-[color:var(--ink-2)]"
          aria-label="Previous month"
          data-testid="calendar-month-prev"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="flex items-center gap-2">
          <div className="font-display text-[15px] md:text-base tracking-tight capitalize">
            {monthLabel}
          </div>
          {/* "Back to today" — only renders when the family is browsing
              another month. Snaps both the grid AND the selectedDay
              back to today so the events list updates too. */}
          {(month !== today.getMonth() || year !== today.getFullYear()) && (
            <button
              type="button"
              onClick={() => {
                setAnchor(new Date(today.getFullYear(), today.getMonth(), 1));
                onSelectDay(keyFor(today));
              }}
              className="text-[11px] uppercase tracking-[0.06em] px-2 py-0.5 rounded-full border border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
              data-testid="calendar-month-today"
            >
              Today
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="w-7 h-7 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center text-[color:var(--ink-2)]"
          aria-label="Next month"
          data-testid="calendar-month-next"
        >
          <ChevronRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
      <>
          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] uppercase tracking-[0.06em] text-[color:var(--ink-3)] mb-0.5">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div key={i} className="py-0.5">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((cell, i) => {
          if (cell === null) {
            return <div key={`blank-${i}`} className="h-7 md:h-8" />;
          }
          const d = cell.day;
          const date = cell.date;
          const k = keyFor(date);
          const info = dayInfo.get(k);
          const occupied = !!info;
          const count = info ? info.count : 0;
          const tint = info ? info.tint : null;
          const todayCell = isToday(date);
          const isSelected = selectedDay === k;
          const handleClick = () => {
            // Empty days for parents → directly open the Add form
            // pre-filled with that date. Otherwise just select the day
            // so the events list narrows.
            if (!occupied && canAdd) {
              onAddForDate(k);
              return;
            }
            onSelectDay(k);
          };
          const ariaLabel = occupied
            ? `${k} — ${count} event${count === 1 ? "" : "s"}`
            : canAdd
              ? `${k} — add event on this day`
              : k;
          const titleAttr = (() => {
            if (!occupied) return canAdd ? "Tap to add an event" : "";
            // Build a multi-line summary of the day's events. Sorted
            // chronologically, time-prefixed when the event isn't
            // an all-day, capped at 5 entries so a 10-event day
            // doesn't overflow the browser tooltip. Uses real
            // newlines (`\n`) — every browser respects them inside
            // `title`. Falls back to the bare count if `titles` is
            // empty (shouldn't happen, defensive).
            const items = (info.titles || [])
              .slice()
              .sort((a, b) => (a.startsAt || "").localeCompare(b.startsAt || ""));
            if (!items.length) return `${count} event${count === 1 ? "" : "s"}`;
            const dateLabel = date.toLocaleDateString("en-US", {
              weekday: "short",
              day: "numeric",
              month: "short",
            });
            const lines = [];
            const visible = items.slice(0, 5);
            for (const it of visible) {
              let prefix = "";
              try {
                const sd = new Date(it.startsAt);
                if (!Number.isNaN(sd.getTime())) {
                  // All-day events have hour=minute=0 (midnight) —
                  // skip the time prefix in that case so the tooltip
                  // doesn't read "0:00  Birthday".
                  if (sd.getHours() !== 0 || sd.getMinutes() !== 0) {
                    prefix = `${fmtTime(sd)} · `;
                  }
                }
              } catch {/* ignore — fall back to no prefix */}
              lines.push(`${prefix}${it.title}`);
            }
            const more = items.length - visible.length;
            if (more > 0) lines.push(`+ ${more} more`);
            return `${dateLabel} — ${count} event${count === 1 ? "" : "s"}\n${lines.join("\n")}`;
          })();
          // Selected/today states win over kind tint visually — they're
          // what tells the user "you're focused here". For occupied,
          // unfocused days we apply a soft kind-tinted background +
          // crisp text so the day type is scannable at a glance.
          const baseClasses =
            "h-7 md:h-8 text-[13px] font-medium tabular relative flex items-center justify-center transition-colors cursor-pointer";
          let bgStyle = {};
          let cls = "";
          // Multi-day "bar" continuity. A cell connects left if the
          // previous cell IN THE SAME WEEK ROW (so we don't bridge
          // Sunday→Monday) shares at least one multi-day event id.
          // Ditto right. We collapse the gap with a -ml-0.5 negative
          // margin and shave the matching corner radii so the tinted
          // background reads as one continuous strip — Google-style.
          // Selected/today cells stay self-contained (sharp focus).
          const colInRow = i % 7;
          const prevCell = colInRow > 0 ? cells[i - 1] : null;
          const nextCell = colInRow < 6 ? cells[i + 1] : null;
          const prevInfo = prevCell ? dayInfo.get(keyFor(prevCell.date)) : null;
          const nextInfo = nextCell ? dayInfo.get(keyFor(nextCell.date)) : null;
          const connectLeft = !!(
            occupied && info && info.eventIds && info.eventIds.size > 0
            && prevInfo && prevInfo.eventIds && [...info.eventIds].some((id) => prevInfo.eventIds.has(id))
          );
          const connectRight = !!(
            occupied && info && info.eventIds && info.eventIds.size > 0
            && nextInfo && nextInfo.eventIds && [...info.eventIds].some((id) => nextInfo.eventIds.has(id))
          );
          const radiusOverride = (() => {
            if (isSelected || todayCell) return "rounded-lg"; // never bar-merge focus cells
            if (connectLeft && connectRight) return "rounded-none";
            if (connectLeft) return "rounded-l-none rounded-r-lg";
            if (connectRight) return "rounded-r-none rounded-l-lg";
            return "rounded-lg";
          })();
          const bridgeMargin = (() => {
            if (isSelected || todayCell) return "";
            // Negative margin in tailwind: -ml-0.5 = -2px, matching
            // the grid's gap-0.5. Cells that should join their left
            // neighbour cancel that gap; nextCell is unaffected and
            // continues the same tint, producing a continuous strip.
            return connectLeft ? "-ml-0.5" : "";
          })();
          if (isSelected) {
            cls = "bg-[color:var(--accent)] text-[color:var(--bg)]";
          } else if (todayCell) {
            cls = "bg-[color:var(--ink)] text-[color:var(--bg)]";
          } else if (occupied) {
            // Two-tone split when there are at least 2 distinct event
            // kinds on this day — top half = dominant, bottom = next.
            // Single-kind days stay solid so the cell doesn't look
            // gimmicky when nothing's mixed. We use a hard 50/50
            // gradient (no blur band) so each kind is unmistakable.
            if (info && info.secondaryTint) {
              bgStyle = {
                background: `linear-gradient(180deg, ${tint} 0%, ${tint} 50%, ${info.secondaryTint} 50%, ${info.secondaryTint} 100%)`,
              };
            } else {
              bgStyle = { background: tint };
            }
            cls = "text-[color:var(--ink)] hover:brightness-95";
          } else {
            cls = "text-[color:var(--ink-3)] hover:bg-[color:var(--bg-soft)] hover:text-[color:var(--ink)]";
          }
          return (
            <button
              key={k}
              type="button"
              onClick={handleClick}
              onDragOver={(e) => {
                if (!onDropEvent) return;
                if (e.dataTransfer && e.dataTransfer.types.includes("application/x-famfeel-event")) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }
              }}
              onDrop={(e) => {
                if (!onDropEvent) return;
                const id = e.dataTransfer.getData("application/x-famfeel-event");
                if (!id) return;
                e.preventDefault();
                onDropEvent(id, k);
              }}
              className={`${baseClasses} ${radiusOverride} ${bridgeMargin} ${cls}`}
              style={bgStyle}
              aria-label={ariaLabel}
              title={titleAttr}
              data-testid={`calendar-month-day-${k}`}
            >
              {d}
              {info && info.hasAttachment && (
                <span
                  className="absolute top-0 right-0.5 text-[8px] leading-none pointer-events-none"
                  aria-hidden="true"
                  data-testid={`calendar-month-attachment-${k}`}
                  title="Photo attached on this day"
                >
                  📎
                </span>
              )}
              {info && info.bands && info.bands.length > 0 && (
                <span
                  className="absolute left-1 right-1 bottom-0.5 h-[2px] rounded-full pointer-events-none"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--ink) 22%, transparent)",
                  }}
                  aria-hidden="true"
                  data-testid={`calendar-month-band-${k}`}
                  title={info.bands.map((b) => b.title).join(" · ")}
                />
              )}
            </button>
          );
        })}
          </div>
        </>
    </div>
  );
}

/**
 * WeekStrip — quick horizontal week view: today + next 6 days.
 *
 * Lives directly under the month grid, gives the family a one-tap
 * shortcut to bounce between the next 7 days without page-flipping
 * the month. Each pill shows the weekday letter, the day number, and
 * a small dot when there's at least one event scheduled.
 *
 * Why "today + next 6" rather than calendar-week (Mon→Sun)? Because
 * the family's mental model on a Saturday morning is "what's the next
 * 7 days look like," not "what's left of this week." Saturday-Friday
 * is more useful than a Mon-locked window that runs out by Wednesday.
 */
function WeekStrip({ events, selectedDay, onSelectDay }) {
  const today = new Date();
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({ key: k, date: d });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const d = new Date(ev.starts_at);
      if (isNaN(d.getTime())) continue;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      map.set(k, (map.get(k) || 0) + 1);
    }
    return map;
  }, [events]);

  return (
    <div
      className="mb-3 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1"
      data-testid="calendar-week-strip"
    >
      {days.map(({ key, date }, i) => {
        const isSel = selectedDay === key;
        const isToday = i === 0;
        const count = eventsByDay.get(key) || 0;
        const dow = date.toLocaleDateString("en-US", { weekday: "short" });
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelectDay(key)}
            className={[
              "flex-shrink-0 flex flex-col items-center justify-center w-12 h-14 rounded-xl transition-colors border",
              isSel
                ? "bg-[color:var(--accent)] text-[color:var(--bg)] border-[color:var(--accent)]"
                : isToday
                  ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                  : "bg-[color:var(--surface)] text-[color:var(--ink)] border-[color:var(--line-soft)] hover:bg-[color:var(--bg-soft)]",
            ].join(" ")}
            aria-label={`${dow} ${date.getDate()} — ${count} event${count === 1 ? "" : "s"}`}
            data-testid={`calendar-week-day-${key}`}
          >
            <span className="text-[10px] uppercase tracking-[0.06em] leading-none mb-1">
              {dow}
            </span>
            <span className="text-base font-display tabular leading-none">
              {date.getDate()}
            </span>
            {count > 0 && (
              <span
                className={`mt-1 w-1.5 h-1.5 rounded-full ${
                  isSel || isToday ? "bg-[color:var(--bg)]" : "bg-[color:var(--ink)]"
                }`}
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}



/**
 * RecurrencePicker — preset row + inline weekday selector when "Custom".
 * Emits a canonical RRULE string (or "" / null when "Doesn't repeat").
 */
function RecurrencePicker({ value, onChange }) {
  const { t, lang } = useI18n();
  const preset = presetForRrule(value);
  const isCustomActive = preset === "custom";
  const { byday: currentByday, count, until } = parseRrule(value);
  const summary = summarizeRrule(value, lang);
  const hasRule = !!value;
  // End-mode derived from current rule. When user toggles the picker
  // we mutate count/until on the existing rule via withRruleEnd.
  const endMode = count ? "count" : until ? "until" : "never";
  // Folded by default for one-off events (most common case) — the
  // editor already has 8+ controls, hiding "Repeats" until the user
  // asks for it cuts the visual noise. If the event already has a
  // rule, we open the fold so the user can see / edit it.
  const [open, setOpen] = useState(hasRule);
  const setEnd = (mode, payload = {}) => {
    if (!hasRule) return;
    if (mode === "never") onChange(withRruleEnd(value, { count: null, until: null }));
    else if (mode === "count") onChange(withRruleEnd(value, { count: payload.count || 10, until: null }));
    else if (mode === "until") onChange(withRruleEnd(value, { count: null, until: payload.until || todayPlus(90) }));
  };
  return (
    <div data-testid="event-recurrence-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="label-tiny inline-flex items-center gap-1.5 hover:text-[color:var(--ink)] transition-colors"
        data-testid="event-recurrence-toggle"
        aria-expanded={open}
      >
        <Repeat className="w-3.5 h-3.5" strokeWidth={2} />
        <span>{t({ en: "Repeats", da: "Gentages" })}</span>
        <span className="ml-1 text-[color:var(--ink-2)] normal-case tracking-normal">
          · {summary || t({ en: "doesn't repeat", da: "gentages ikke" })}
        </span>
        <span aria-hidden="true" className="ml-0.5">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1.5">
            {CADENCE_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => onChange(p.rrule || "")}
                className={`ff-chip ${preset === p.key ? "is-active" : ""}`}
                data-testid={`event-cadence-${p.key}`}
              >
                {cadencePresetLabel(p, lang)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onChange(buildRruleFromCustom({ freq: "WEEKLY", byday: currentByday.length ? currentByday : ["MO"] }))}
              className={`ff-chip ${isCustomActive ? "is-active" : ""}`}
              data-testid="event-cadence-custom"
            >
              {t({ en: "Custom days…", da: "Egne dage…" })}
            </button>
          </div>
      {isCustomActive && (
        <div className="flex flex-wrap items-center gap-1 mt-2" data-testid="event-cadence-byday">
          {WEEKDAY_TOKENS.map((tok, i) => {
            const active = currentByday.includes(tok);
            return (
              <button
                key={tok}
                type="button"
                onClick={() => {
                  const next = active
                    ? currentByday.filter((d) => d !== tok)
                    : [...currentByday, tok];
                  if (next.length === 0) {
                    onChange("");
                  } else {
                    const { freq = "WEEKLY", interval = 1, count: c, until: u } = parseRrule(value);
                    onChange(buildRruleFromCustom({ freq, interval, byday: next, count: c, until: u }));
                  }
                }}
                className={`ff-chip w-9 justify-center ${active ? "is-active" : ""}`}
                aria-pressed={active}
                data-testid={`event-byday-${tok}`}
              >
                {WEEKDAY_LABELS[i]}
              </button>
            );
          })}
        </div>
      )}
      {hasRule && (
        <div className="mt-3" data-testid="event-recurrence-end">
          <div className="label-tiny mb-1.5">{t({ en: "Ends", da: "Slutter" })}</div>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setEnd("never")}
              className={`ff-chip ${endMode === "never" ? "is-active" : ""}`}
              data-testid="event-recurrence-end-never"
            >
              {t({ en: "Never", da: "Aldrig" })}
            </button>
            <button
              type="button"
              onClick={() => setEnd("count", { count: count || 10 })}
              className={`ff-chip ${endMode === "count" ? "is-active" : ""}`}
              data-testid="event-recurrence-end-after"
            >
              {t({ en: "After…", da: "Efter…" })}
            </button>
            <button
              type="button"
              onClick={() => setEnd("until", { until: until || todayPlus(90) })}
              className={`ff-chip ${endMode === "until" ? "is-active" : ""}`}
              data-testid="event-recurrence-end-until"
            >
              {t({ en: "On date…", da: "På dato…" })}
            </button>
            {endMode === "count" && (
              <span className="inline-flex items-center gap-1.5 ml-1">
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={count || ""}
                  onChange={(e) => setEnd("count", { count: Number(e.target.value) || 1 })}
                  className="ff-input w-20 py-1.5 text-[13px] tabular"
                  data-testid="event-recurrence-end-count"
                />
                <span className="text-[12px] text-[color:var(--ink-3)]">{t({ en: "times", da: "gange" })}</span>
              </span>
            )}
            {endMode === "until" && (
              <input
                type="date"
                value={until || ""}
                onChange={(e) => setEnd("until", { until: e.target.value })}
                className="ff-input w-auto py-1.5 text-[13px] tabular"
                data-testid="event-recurrence-end-until-date"
              />
            )}
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * ScopePickerDialog — "this / future / all" prompt for editing or
 * deleting a recurring event instance.
 */
function ScopePickerDialog({ kind, onCancel, onPick }) {
  const verb = kind === "delete" ? "Delete" : "Save changes for";
  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/55 backdrop-blur-sm flex items-center justify-center p-5"
      onClick={onCancel}
      data-testid="scope-picker-backdrop"
    >
      <div
        className="bg-[color:var(--bg)] rounded-[14px] max-w-sm w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="scope-picker-dialog"
      >
        <div className="font-display text-2xl tracking-tight leading-tight">
          {verb} which?
        </div>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => onPick("this")}
            className="ff-btn w-full text-left justify-start"
            data-testid="scope-pick-this"
          >
            Just this event
          </button>
          <button
            type="button"
            onClick={() => onPick("future")}
            className="ff-btn w-full text-left justify-start"
            data-testid="scope-pick-future"
          >
            This and future events
          </button>
          <button
            type="button"
            onClick={() => onPick("all")}
            className="ff-btn ff-btn-primary w-full text-left justify-start"
            data-testid="scope-pick-all"
          >
            All events in the series
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[12px] text-[color:var(--ink-3)] mt-2 inline-block w-full text-center hover:text-[color:var(--ink)]"
            data-testid="scope-pick-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
